import { Component, Input, OnChanges } from '@angular/core';
import { Product } from '../../../model/product/product.model';
import { mixHex } from '../line-electric-colors';
import { LEAF_BLADE } from '../line-tree-glyph';
import { MAPLE_BLADE } from '../line-maple-glyph';
import { FAN_FRONDS } from '../line-palm-glyph';
import { BIRDS, BIRD_COLORS, birdD as gull } from '../line-bird-glyph';
import { MIAMI_FRONDS, MIAMI_FROND_REACH } from '../line-miami-palm-glyph';
import { EIFFEL_SOLID } from '../line-eiffel-glyph';
import { SNOWFLAKE, SNOWFLAKE_SIMPLE } from '../line-snowflake-glyph';
import { JELLYFISH, JELLYFISH_SIMPLE } from '../line-jellyfish-glyph';
import { SHELL, SHELL_RIBS } from '../line-shell-glyph';
import { LAMP_GLASS, LAMP_MOTE, LAMP_SIMPLE } from '../line-lamp-glyph';
import { YACHT, YACHT_RIPPLE, YACHT_SIMPLE } from '../line-yacht-glyph';
import { BASALT_HEX } from '../line-basalt-glyph';
import { FLOWER_FOLIAGE_JOINED, FLOWER_GREEN, FLOWER_HEAD } from '../line-meadow-flower-glyph';
import { NOTE } from '../line-violin-glyph';
import { CLAVEL, CLAVEL_SIMPLE } from '../line-clavel-glyph';
import { FUJI_MOUNT, FUJI_SNOW } from '../line-pagoda-glyph';
import {
  PARASOL_PANELS_A, PARASOL_PANELS_B, PARASOL_PIVOT, PARASOL_POLE,
} from '../line-parasol-glyph';

interface Petal {
  transform: string; fill: string; o: number;
  /** Per-grain geometry, when the design draws a different shape each time
   *  (Everest's contour rings) instead of one shared silhouette. */
  d?: string;
  /** Stroke width — set it and the grain is drawn as an outline, not a fill. */
  sw?: number;
}

/**
 * The LINE "talk room background" wallpaper (i_22 iOS / a_22 Android), drawn as
 * a MINIMAL kawaii scene: soft cherry-blossom petals drifting in the wind over
 * an EMPTY (fully transparent) canvas (owner 2026-07-29: petals only, no whole
 * blossoms).
 *
 * Why transparent (owner + LINE guideline "F トークルーム背景画像", option
 * "背景画像を透過で作成する"): LINE centers the wallpaper vertically and, on
 * tall screens (iPhone 16 Pro), the top of a centered OPAQUE image is cut and
 * the flat COLOR SKIN shows through — a visible seam under the nav bar. If the
 * wallpaper is transparent, the color skin becomes the uniform "sky" everywhere
 * (the top strip AND behind the petals), so no seam can ever appear on any
 * device. The flat pink to set as the color-skin talk-room background is
 * `skinColor` below (== the intended sky). Petals keep a clear margin from every
 * edge so no single petal is hard-clipped when the image is centered/cut.
 */
@Component( {
  selector: 'app-line-chat-bg',
  templateUrl: './line-chat-bg.component.html',
  styleUrls: [ './line-chat-bg.component.scss' ],
} )
export class LineChatBgComponent implements OnChanges {

  @Input() product!: Product;

  readonly W = 1000;
  readonly H = 900;
  uid = 'cbg';

  /** Unit cherry-blossom petal (base at origin, tip up, tiny V-notch), shared
   *  with line-menu-bg. Reused for drifting petals and the blossoms. */
  readonly petalD = 'M0,0 C-2.2,-1.5 -2.0,-5.0 -0.4,-6.0 L0,-5.4 L0.4,-6.0 C2.0,-5.0 2.2,-1.5 0,0 Z';

  /** Which scene drifts over the flat skin. */
  design: 'sakura' | 'tokyo' | 'everest' | 'aurora' | 'sequoia' | 'autumn' | 'roraima'
    | 'miami' | 'istanbul' | 'paris' | 'rio' | 'tallinn' | 'ocean' | 'salda' | 'prague' | 'sydney' | 'reynisfjara' | 'spring' | 'vienna' | 'madrid' | 'etna' | 'fuji' = 'sakura';

  /**
   * Aurora's curtains. Each is a soft vertical band of light with its own
   * gradient, drawn over TRANSPARENCY so the LINE colour skin is the night sky.
   *
   * This replaces the opaque photo-plus-scrim wallpaper Aurora shipped with:
   * being opaque, it covered the skin's talk-room colour completely, so the
   * owner had no way to see or verify that colour on the phone (2026-08-12).
   * Aurora is also the one theme whose hero subject is ALREADY translucent, so
   * curtains over a transparent canvas is the honest way to draw it.
   */
  curtains: { d: string; grad: string; o: number }[] = [];
  /** Faint stars behind the curtains, as `cx cy r opacity`. */
  stars: { cx: number; cy: number; r: number; o: number }[] = [];
  /** Curtain gradients, one entry per `grad` id: a full stop list so a ribbon
   *  can run through several colours along its length. */
  curtainStops: {
    id: string; x1: number; y1: number; x2: number; y2: number;
    stops: { off: number; color: string; o: number }[];
    /** When set the strand is a flat tint, not a gradient (the realistic
     *  build stacks many flat rays and lets density do the shading). */
    flat?: string;
  }[] = [];
  /**
   * Sequoia: one sprig of redwood foliage — a stem with paired needles, the
   * fragment its own icon is made of. Built rather than hand-written so the
   * needles stay evenly paired at any count.
   */
  private static sprig( n = 7, len = 13, needle = 4.6 ): string {   // kept: unused since the leaf swap
    const f = ( v: number ) => +v.toFixed( 2 );
    const parts = [ `M0,${ f( len * 0.5 ) } L0,${ f( -len * 0.5 ) }` ];
    for ( let i = 0; i < n; i++ ) {
      const t = i / ( n - 1 );
      const y = len * 0.5 - len * t;
      // Needles shorten toward the tip, and sweep forward like real foliage.
      const l = needle * ( 1 - t * 0.55 );
      const dy = -l * 0.55;
      parts.push( `M0,${ f( y ) } L${ f( -l ) },${ f( y + dy ) }` );
      parts.push( `M0,${ f( y ) } L${ f( l ) },${ f( y + dy ) }` );
    }
    return parts.join( ' ' );
  }
  readonly sprigD = LineChatBgComponent.sprig();
  /** Sequoia drifts the SAME leaf its passcode dots draw (owner 2026-08-13), so
   *  the wallpaper and the dots are one object at two sizes — the relationship
   *  Sakura has between its blossom and its petals. Authored in the shared 120
   *  box, so every transform below re-centres it first. */
  readonly leafD = LEAF_BLADE;
  /** Autumn drifts the SAME maple its passcode dots and Home tab draw, traced
   *  from the theme's icon. Authored in the shared 120 box, so every transform
   *  re-centres it first. */
  readonly mapleD = MAPLE_BLADE;
  /** Roraima flies BIRDS over the talk room (line-bird-glyph). Each grain
   *  carries its own wing position via Petal.d, so the flock is birds rather
   *  than one bird repeated. Authored in the shared 120 box, so every transform
   *  re-centres it first. */
  readonly birdD = BIRDS[ 0 ];

  /**
   * Istanbul flies the SAME gull (line-bird-glyph) — nothing is more Istanbul
   * than the birds over the strait — asked for its own wing positions rather
   * than copied into a second glyph. `birdD` already takes the beat and the
   * asymmetry as parameters, so a different flock is a different argument list,
   * and Roraima's `BIRDS` set is untouched by it.
   *
   * Weighted toward the LOW end of the beat: a gull over the Bosphorus hangs on
   * the wind far more than it flaps, so five of these eight are glides and only
   * one is a full upstroke. Roraima's four evenly-spaced lifts read as a flock
   * crossing the savanna; this one reads as a flock loitering.
   */
  private static readonly GULLS: string[] = [
    gull( 0.18, 0.55 ), gull( 0.34, -0.45 ), gull( 0.55, 0.70 ), gull( 0.22, -0.30 ),
    gull( 0.78, -0.60 ), gull( 0.42, 0.25 ), gull( 1.00, 0.45 ), gull( 0.30, 0.65 ),
  ];

  /** Tokyo: a soft 4-point twinkle instead of a petal. */
  readonly sparkleD = 'M0,-6 Q0.7,-0.7 6,0 Q0.7,0.7 0,6 Q-0.7,0.7 -6,0 Q-0.7,-0.7 0,-6 Z';
  /**
   * Everest: ONE KAWAII CLOUD, drawn as a SINGLE continuous outline.
   *
   * The outline is TRACED from the reference cartoon cloud the owner supplied
   * (2026-08-12, ~/Downloads/png-simple-cartoon-cloud-illustration…): its
   * silhouette was masked, boundary-traced and Douglas-Peucker simplified to the
   * 41 points below, normalised so x runs -1..1 about the centre. Hand-guessed
   * lobes kept missing it — a big dome left of centre, a stepped shoulder down
   * the right side, and a scalloped underside are all load-bearing, and eyeballed
   * circles flattened exactly those.
   *
   * Rendered with quadratics through segment MIDPOINTS, which is
   * tangent-continuous everywhere, so the traced polygon comes back out as one
   * unbroken curve with no facets and no creases.
   *
   * `s` is the half-width: the cloud is 2s wide and ~1.2s tall. Per-cloud variety
   * comes from mirroring and a few percent of stretch, never from moving points
   * around — that would just undo the trace.
   */
  private static readonly CLOUD_RING: [ number, number ][] = [
    [ -0.036, -0.605 ], [ 0.072, -0.602 ], [ 0.198, -0.56 ], [ 0.333, -0.449 ],
    [ 0.411, -0.317 ], [ 0.547, -0.32 ], [ 0.649, -0.281 ], [ 0.73, -0.209 ],
    [ 0.781, -0.107 ], [ 0.853, -0.089 ], [ 0.916, -0.05 ], [ 0.97, 0.014 ],
    [ 1.0, 0.104 ], [ 0.994, 0.188 ], [ 0.949, 0.284 ], [ 0.85, 0.383 ],
    [ 0.697, 0.461 ], [ 0.571, 0.488 ], [ 0.441, 0.491 ], [ 0.258, 0.458 ],
    [ 0.12, 0.548 ], [ -0.036, 0.596 ], [ -0.195, 0.602 ], [ -0.36, 0.566 ],
    [ -0.477, 0.599 ], [ -0.61, 0.605 ], [ -0.742, 0.578 ], [ -0.835, 0.53 ],
    [ -0.949, 0.41 ], [ -1.0, 0.272 ], [ -0.997, 0.131 ], [ -0.931, -0.017 ],
    [ -0.832, -0.107 ], [ -0.703, -0.152 ], [ -0.67, -0.251 ], [ -0.586, -0.335 ],
    [ -0.477, -0.368 ], [ -0.384, -0.353 ], [ -0.321, -0.446 ], [ -0.246, -0.518 ],
    [ -0.141, -0.578 ],
  ];

  private static cloud( s: number, seed: number ): string {
    const hash = ( i: number ): number => {
      const v = Math.sin( seed * 12.9898 + i * 78.233 ) * 43758.5453;
      return v - Math.floor( v );
    };
    // Mirror half of them and stretch each by a few percent, so a wallpaper of
    // one traced silhouette never reads as a repeated stamp.
    const flip = hash( 0 ) < 0.5 ? -1 : 1;
    const sx = s * flip * ( 0.97 + hash( 1 ) * 0.06 );
    const sy = s * ( 0.96 + hash( 2 ) * 0.08 );

    const ring = LineChatBgComponent.CLOUD_RING;
    const n = ring.length;
    const f = ( v: number ) => +v.toFixed( 1 );
    const pts = ring.map( ( [ x, y ] ) => [ x * sx, y * sy ] );
    const mid = ( a: number[], b: number[] ) => [ ( a[ 0 ] + b[ 0 ] ) / 2, ( a[ 1 ] + b[ 1 ] ) / 2 ];
    const m0 = mid( pts[ n - 1 ], pts[ 0 ] );
    let d = `M${ f( m0[ 0 ] ) },${ f( m0[ 1 ] ) }`;
    for ( let i = 0; i < n; i++ ) {
      const c = pts[ i ];
      const m = mid( pts[ i ], pts[ ( i + 1 ) % n ] );
      d += ` Q${ f( c[ 0 ] ) },${ f( c[ 1 ] ) } ${ f( m[ 0 ] ) },${ f( m[ 1 ] ) }`;
    }
    return d + ' Z';
  }
  /** The drifting shape for the current design. Everest carries per-grain
   *  geometry instead (Petal.d), so it never reaches this. */
  get shapeD(): string {
    return this.design === 'tokyo' ? this.sparkleD
      : this.design === 'sequoia' ? this.leafD
        : this.design === 'autumn' ? this.mapleD
          : this.design === 'roraima' ? this.birdD
            : this.design === 'istanbul' ? LineChatBgComponent.GULLS[ 0 ]
        : this.petalD;
  }

  /** The flat pink the LINE color skin's talk-room background should be set to
   *  so the "sky" is uniform and seamless (this component draws NO sky itself). */
  skinColor = '#FCEAF2';

  petals: Petal[] = [];

  /** Etna's embers: a blurred halo and a sharp core per spark. */
  embers: { cx: number; cy: number; halo: number; core: number;
            fill: string; coreFill: string; ho: number; co: number }[] = [];

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    this.uid = 'cbg-' + ( p.key || 'x' );
    if ( p.key === 'tokyo' ) { this.design = 'tokyo'; this.buildTokyo( p ); return; }
    if ( p.key === 'everest' ) { this.design = 'everest'; this.buildEverest( p ); return; }
    if ( p.key === 'aurora-borealis' ) { this.design = 'aurora'; this.buildAurora( p ); return; }
    if ( p.key === 'sequoia' ) { this.design = 'sequoia'; this.buildSequoia( p ); return; }
    if ( p.key === 'autumn' ) { this.design = 'autumn'; this.buildAutumn( p ); return; }
    if ( p.key === 'roraima' ) { this.design = 'roraima'; this.buildRoraima( p ); return; }
    if ( p.key === 'miami' ) { this.design = 'miami'; this.buildMiami( p ); return; }
    if ( p.key === 'istanbul' ) { this.design = 'istanbul'; this.buildIstanbul( p ); return; }
    if ( p.key === 'paris' ) { this.design = 'paris'; this.buildParis( p ); return; }
    if ( p.key === 'rio-de-janeiro' ) { this.design = 'rio'; this.buildRio( p ); return; }
    if ( p.key === 'tallinn' ) { this.design = 'tallinn'; this.buildTallinn( p ); return; }
    if ( p.key === 'ocean' ) { this.design = 'ocean'; this.buildOcean( p ); return; }
    if ( p.key === 'salda' ) { this.design = 'salda'; this.buildSalda( p ); return; }
    if ( p.key === 'prague' ) { this.design = 'prague'; this.buildPrague( p ); return; }
    if ( p.key === 'sydney' ) { this.design = 'sydney'; this.buildSydney( p ); return; }
    if ( p.key === 'reynisfjara' ) { this.design = 'reynisfjara'; this.buildReynisfjara( p ); return; }
    if ( p.key === 'spring' ) { this.design = 'spring'; this.buildSpring( p ); return; }
    if ( p.key === 'vienna' ) { this.design = 'vienna'; this.buildVienna( p ); return; }
    if ( p.key === 'madrid' ) { this.design = 'madrid'; this.buildMadrid( p ); return; }
    if ( p.key === 'etna' ) { this.design = 'etna'; this.buildEtna( p ); return; }
    if ( p.key === 'fuji' ) { this.design = 'fuji'; this.buildFuji( p ); return; }
    this.design = 'sakura';
    this.buildSakura( p );
  }

  /**
   * Aurora: three light curtains rising out of the lower half, plus a scatter of
   * faint stars. Every pixel is either a translucent curtain or nothing at all,
   * so the exported PNG keeps a large clear area and the LINE skin shows through
   * as the night sky — which is the whole point of the redraw (see `curtains`).
   *
   * A curtain is not one wide band — that just blurs into a blob. It is a run of
   * NARROW vertical ribbons sharing a colour, each with its own width, height and
   * sway, fading to nothing at both ends. Blurred together they read as one
   * rayed sheet of light, which is how the real thing looks, and the gaps
   * between runs leave clear sky.
   */
  private buildAurora( p: Product ): void {
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    // Sky = the LINE skin's near-black; the curtains are the theme's own greens,
    // teals and violets.
    this.skinColor = '#131313';
    const green = cp[ 2 ] || t.secondaryColor || '#1ECFB6';
    const blue = cp[ 1 ] || t.primaryColor || '#549EFF';
    const violet = cp[ 4 ] || '#BAA5FF';

    // Straight 45-DEGREE shafts of light, pointed at BOTH ends and lying fully
    // inside the canvas so you can see where each one begins and ends (owner
    // 2026-08-12). The upright ribbon curtains this replaces ran off the edges,
    // so they had no shape of their own; the taper is the single thing that
    // reads as light rather than as a drawn stroke.
    //
    // Every shaft shares one direction — up and to the right at exactly 45° —
    // which is what makes a scatter of them read as one weather rather than as
    // separate marks.
    const shaft = ( x0: number, y0: number, len: number ): number[] => {
      const d = len / Math.SQRT2;                 // 45°: equal run and rise
      const x3 = x0 + d, y3 = y0 - d;
      // Collinear controls keep the centre line dead straight.
      return [
        x0, y0,
        x0 + ( x3 - x0 ) / 3, y0 + ( y3 - y0 ) / 3,
        x0 + ( x3 - x0 ) * 2 / 3, y0 + ( y3 - y0 ) * 2 / 3,
        x3, y3,
      ];
    };
    // The MENU BACKGROUND's band construction, turned 45° (owner 2026-08-13).
    // That strip is the version of this theme's aurora everyone already signed
    // off on: several long wavy bands whose centre line and thickness are both
    // integer-frequency sines, layered over a wide faint wash, blurred and kept
    // semi-transparent so they overlap into one sheet. The only change here is
    // the frame — u runs up a 45° axis and v swings across it, so the whole
    // sheet lies on the diagonal instead of across the strip.
    //
    // Colours stay pale rather than poster-bright: the brightest point of the
    // owner's reference photograph is #8ED3A6, and saturated bands are what made
    // the earlier passes read as a colouring book.
    const DX = Math.SQRT1_2, DY = -Math.SQRT1_2;   // 45°, up and to the right
    const NX = Math.SQRT1_2, NY = Math.SQRT1_2;    // across the axis
    const OX = -180, OY = 1080;                    // start off-canvas, bottom-left
    const LEN = 1500;                              // long enough to cross the canvas
    // A touch more saturated than the photo's #8ED3A6 (owner 2026-08-13:
    // "birazcık daha canlı") — still short of the theme's full #1ECFB6, which
    // is what tipped the earlier passes into poster paint.
    const MINT = '#63D9AE';
    const band = ( base: number, amp: number, k: number, phase: number,
                   th: number, color: string, o: number ) => {
      const N = 120;
      const f = ( x: number ) => +x.toFixed( 1 );
      const top: string[] = [];
      const bot: string[] = [];
      for ( let i = 0; i <= N; i++ ) {
        const t2 = i / N;
        const u = LEN * t2;
        const v = base + amp * Math.sin( 2 * Math.PI * k * t2 + phase );
        const h = ( th / 2 ) * ( 1 + 0.34 * Math.sin( 2 * Math.PI * ( k + 1 ) * t2 + phase * 1.7 ) );
        const px = OX + u * DX + v * NX;
        const py = OY + u * DY + v * NY;
        top.push( `${ f( px + NX * h ) },${ f( py + NY * h ) }` );
        bot.push( `${ f( px - NX * h ) },${ f( py - NY * h ) }` );
      }
      return { d: `M${ top.join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`, fill: color, o };
    };
    const bands = [
      // the wide faint wash that keeps the middle of the sheet filled
      band( 40, 26, 1, 1.6, 210, blue, 0.15 ),
      band( -34, 46, 1, 0.4, 86, MINT, 0.38 ),
      band( 22, 58, 1, 2.3, 96, violet, 0.3 ),
      band( 62, 50, 1, 4.0, 92, blue, 0.33 ),
      band( 104, 44, 2, 1.1, 78, MINT, 0.32 ),
      band( 150, 36, 2, 3.4, 60, violet, 0.23 ),
    ];
    this.curtainStops = bands.map( ( b2, i ) => ( {
      id: `b${ i }`, x1: 0, y1: 0, x2: 0, y2: 0, stops: [], flat: b2.fill,
    } ) );
    this.curtains = bands.map( ( b2, i ) => ( { d: b2.d, grad: `b${ i }`, o: b2.o } ) );

    // Stars, scattered with an independent hash PER AXIS. The golden-ratio
    // sequence this replaces advanced x and y in lockstep, so the "random"
    // scatter drew faint diagonal rows across the wallpaper (owner 2026-08-12).
    const rnd = ( i: number, k: number ): number => {
      const v = Math.sin( ( i + 1 ) * 12.9898 + k * 78.233 ) * 43758.5453;
      return v - Math.floor( v );
    };
    this.stars = [];
    for ( let i = 0; i < 54; i++ ) {
      const x = Math.round( 30 + rnd( i, 1 ) * ( this.W - 60 ) );
      const y = Math.round( 26 + rnd( i, 2 ) * ( this.H - 52 ) );
      const r = +( 1.2 + rnd( i, 3 ) * 2.8 ).toFixed( 1 );
      this.stars.push( { cx: x, cy: y, r, o: +( 0.2 + rnd( i, 4 ) * 0.45 ).toFixed( 2 ) } );
    }
    this.petals = [];
  }

  /** A cubic turned into a band that comes to a POINT at both ends — half-width
   *  follows sin(pi*t)^0.45, so it stays broad through the middle and closes on
   *  a tip at each end. */
  private static spindle( p: number[], w: number ): string {
    const N = 34;
    const at = ( t: number ) => {
      const u = 1 - t;
      const b = [ u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t ];
      return [
        b[ 0 ] * p[ 0 ] + b[ 1 ] * p[ 2 ] + b[ 2 ] * p[ 4 ] + b[ 3 ] * p[ 6 ],
        b[ 0 ] * p[ 1 ] + b[ 1 ] * p[ 3 ] + b[ 2 ] * p[ 5 ] + b[ 3 ] * p[ 7 ],
      ];
    };
    const f = ( v: number ) => +v.toFixed( 1 );
    const top: string[] = [];
    const bot: string[] = [];
    for ( let i = 0; i <= N; i++ ) {
      const t = i / N;
      const [ x, y ] = at( t );
      const [ nx, ny ] = at( Math.min( 1, t + 0.001 ) );
      const dx = nx - x, dy = ny - y;
      const len = Math.hypot( dx, dy ) || 1;
      const h = ( w / 2 ) * Math.pow( Math.sin( Math.PI * t ), 0.45 );
      top.push( `${ f( x + ( -dy / len ) * h ) },${ f( y + ( dx / len ) * h ) }` );
      bot.push( `${ f( x - ( -dy / len ) * h ) },${ f( y - ( dx / len ) * h ) }` );
    }
    return `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
  }

  /**
   * Autumn: maple leaves falling across the chat skin — the theme's own icon
   * leaf, the same one its passcode dots and Home tab carry. Built on SAKURA's
   * structure, which is the drift this whole family is tuned around: a jittered
   * grid so coverage stays even without anything sitting on a grid line, and
   * rotation clustered about one prevailing wind angle so the leaves tumble
   * together rather than pointing every which way.
   *
   * PROVISIONAL like the rest of Autumn: the sky here is the theme's own cream,
   * not a sampled LINE skin, and revision 2 replaces it.
   */
  private buildAutumn( p: Product ): void {
    // The flat sky to paste into LINE's talk-room field: the cream of the skin
    // the owner picked (sampled 2026-08-13), not the theme's own editor cream.
    this.skinColor = '#FFF1DD';
    // LIGHT ORANGES carry the drift and red is now the exception, one tint in
    // seven (owner 2026-08-13). The first pass ran four reddish tints out of
    // seven — ember twice, plus bark and a rust — which turned the wallpaper
    // heavy and, on this cream sky, closer to brown than to autumn light.
    const tints = [ '#F2B45C', '#EFA83A', '#F5C078', '#DEA51D', '#E8B96B', '#F0A855', '#C7683C' ];

    const fr = ( v: number ) => v - Math.floor( v );
    const mx = 70, my = 80;
    const windAngle = 35;              // degrees, the prevailing drift direction
    const cols = 6, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];
    // Thin the drift by 10% (owner 2026-08-13). Three of the thirty cells are
    // dropped, spaced across the grid rather than taken off one edge, so the
    // wallpaper loses density without opening a bald patch.
    const dropped = new Set( [ 4, 15, 23 ] );
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        if ( dropped.has( i ) ) { continue; }
        const jx = 0.12 + fr( i * 0.6180339887 + 0.11 ) * 0.76;
        const jy = 0.12 + fr( i * 0.7548776662 + 0.37 ) * 0.76;
        const x = Math.round( mx + ( c + jx ) * cellW );
        const y = Math.round( my + ( r + jy ) * cellH );
        const rot = Math.round( windAngle + ( fr( i * 0.5497004779 + 0.5 ) - 0.5 ) * 150 );
        const sc = +( 0.24 + fr( i * 0.3819660112 + 0.2 ) * 0.26 ).toFixed( 3 );
        this.petals.push( {
          transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc }) translate(-60,-60)`,
          fill: tints[ i % tints.length ],
          // Bigger leaves are nearer → a touch more opaque. Carried up a step
          // with the palette: the lighter tints sit closer to the cream sky, so
          // the old 0.30–0.55 range would have thinned them twice over.
          o: +( 0.4 + ( sc - 0.24 ) / 0.26 * 0.28 ).toFixed( 2 ),
        } );
      }
    }
  }

  /**
   * Roraima: a flock of BIRDS over the talk room.
   *
   * Two motifs were built and thrown out first. The theme's own tepui scattered
   * at every scale was a mountain hanging in mid-air ("uçan dağlar saçma bir
   * fikir"), and moriche fronds drifted honestly but belonged to a different
   * picture. A bird is the one subject here that is genuinely in the air, which
   * is the whole thing the wallpaper needs and the thing both earlier passes got
   * wrong.
   *
   * Colours are the theme's OWN sunset palette, not tints of the skin — birds
   * mixed toward the brown vanished into it.
   *
   * Same structure as every other drift in this family: Sakura's jittered grid,
   * one grain per cell, size carrying the depth. The tilt stays SMALL, unlike
   * the leaves and petals: a bird banks a few degrees, it does not tumble, and a
   * gull rotated 90° stops being a bird at all.
   */
  private buildRoraima( p: Product ): void {
    // The flat sky to paste into LINE's talk-room field: the dark brown of the
    // skin the owner picked (sampled 2026-08-13).
    this.skinColor = '#5C3E30';

    // MORICHE FAN FRONDS, drifting (owner 2026-08-14). This replaces the flock
    // of gulls, which was reassigned to Istanbul — nothing is more Istanbul than
    // the gulls over the strait, and Roraima gets back the fragment its own
    // menu strip already plants: the savanna at the foot of the range is moriche
    // palms, so a loose frond is a thing that genuinely blows off them.
    //
    // The leaf is PALMATE and Miami's is pinnate, which is what keeps two palm
    // themes from reading as one (see line-palm-glyph's FAN_FRONDS).
    // ONE knob for "generate another one" (owner 2026-08-14). Math.random is
    // forbidden — it would hand back a different wallpaper on every rebuild and
    // the exported asset would stop matching the preview — so a reshuffle is a
    // phase shift on the seeded noise instead. Change SEED and every position,
    // lean, size and colour moves together, deterministically.
    const SEED = 0.6142;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };
    // IN SHADOW, the rule the gulls established here and the reason they sat
    // behind the bubbles instead of fighting them: the palette hues are carried
    // a long way toward the skin, so the fronds read as leaves against a
    // darkening sky rather than as bright marks pinned on it.
    const shade = ( c: string ) => mixHex( c, this.skinColor, 0.42 );
    const COLORS = [ '#D17458', '#D69568', '#D1BA46', '#DDBE6D', '#CC654E', '#ED8B8B', '#8F78B7' ];

    // The scatter is RELAXED, not just jittered — and that change is the whole
    // story of this layout. A jittered grid with cells dropped out of it was
    // tuned by hand four times and each fix produced the next complaint: a hole
    // in the middle, then a heavy top over an empty foot, then a thin
    // middle-left, then an empty middle-right. That is not bad luck. Jitter can
    // pull two neighbours toward each other and leave the space behind them
    // bare, and dropping cells can only ever remove, never fill.
    //
    // So the leaves push each other apart instead. Start from the staggered
    // grid, then let every leaf repel any neighbour closer than the even spacing
    // for this count, a few passes, clamped inside the margins. The result is
    // blue-noise: evenly spread with no lattice, which is exactly what a scatter
    // wants and what no amount of seed-hunting was going to produce.
    //
    // PARTIALLY relaxed, and the amount is the second half of the tuning. Run to
    // convergence (14 passes) the leaves settle into a near-hexagonal lattice —
    // no holes at all, and far too regular to read as a scatter (owner: "çok
    // eşit simetrik görünüyor"). Three passes at a gentler step keeps most of the
    // starting irregularity while still pulling apart the clumps that leave bare
    // ground behind them.
    //
    // Measured, spread of nearest-neighbour distances as the irregularity index:
    //   14 passes  variance 0.235  irregularity 0.035   <- a lattice
    //    3 passes  variance 0.339  irregularity 0.150   <- this
    // and no region starved either way (weakest 1.08). For scale, the hand-tuned
    // jitter-and-drop version measured variance 1.00 with the centre at 3.91 and
    // the middle-left at 0.79 — the complaints, in the order they arrived.
    //
    // The size range is also tighter than it was, 0.62-0.76 rather than
    // 0.66-0.94. Two reasons. With positions this even the remaining unevenness
    // was coming from SIZE — one big leaf in a region outweighs two small ones
    // and the eye reads that as a heavy corner — and the leaves came down about
    // a tenth on top of that, which is how the density was lightened.
    //
    // Lightening it by REMOVING leaves was measured first and is worse: at 20
    // leaves the weakest region falls to 0.52 and holes open up, where 25 smaller
    // ones hold every region at 0.85 or better. Same ink, no bare ground.
    const mx = 110, my = 120;
    const cols = 5, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    // 1. the staggered, jittered starting positions
    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.1 + fr( i * 0.6180339887 + 0.29 ) * 0.8;
        const jy = 0.1 + fr( i * 0.7548776662 + 0.61 ) * 0.8;
        pts.push( {
          i,
          // Alternate rows offset by half a cell (brickwork), the column index
          // taken modulo the grid so the offset wraps instead of pushing the
          // last leaf off the canvas.
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          s: +( 0.62 + Math.pow( fr( i * 0.3819660112 + 0.2 ), 1.2 ) * 0.14 ).toFixed( 3 ),
        } );
      }
    }

    // 2. relax them apart
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.06;
    for ( let pass = 0; pass < 3; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.16 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.16 ) );
      }
    }

    // 3. draw
    for ( const pt of pts ) {
      const i2 = pt.i;
      const x = Math.round( pt.x ), y = Math.round( pt.y ), s = pt.s;
      // Rotation is bounded at +-55 degrees, and the bound is the point: a
      // falling leaf turns, but its stalk stays broadly downward. Past about 60
      // it stops reading as a leaf and becomes an abstract fan, which is the
      // same failure a gull rotated 90 degrees had.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.5 ) - 0.5 ) * 110 );
      const o = +( 0.58 + fr( i2 * 0.277 ) * 0.3 ).toFixed( 2 );
      // Anchored on the HINGE (60,96), not the box centre, so each leaf turns
      // about its own stalk rather than swinging around a point in mid-air.
      this.petals.push( {
        d: FAN_FRONDS[ i2 % FAN_FRONDS.length ],
        transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ s }) translate(-60,-96)`,
        fill: shade( COLORS[ i2 % COLORS.length ] ), o,
      } );
    }
  }

  /** WCAG relative luminance of a #RRGGBB. */
  private static lum( hexCol: string ): number {
    const ch = ( i: number ): number => {
      const c = parseInt( hexCol.substr( 1 + i * 2, 2 ), 16 ) / 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );
    };
    return 0.2126 * ch( 0 ) + 0.7152 * ch( 1 ) + 0.0722 * ch( 2 );
  }

  /** Contrast ratio between two #RRGGBB, WCAG definition. */
  private static contrast( a: string, b: string ): number {
    const la = LineChatBgComponent.lum( a );
    const lb = LineChatBgComponent.lum( b );
    return ( Math.max( la, lb ) + 0.05 ) / ( Math.min( la, lb ) + 0.05 );
  }

  /**
   * The opacity at which `ink` painted on `sky` lands on exactly `target`
   * contrast — solved, not guessed.
   *
   * A wallpaper has one hard requirement that eyeballing a tint list cannot
   * meet: every grain has to be PRESENT against the skin and none of it may
   * fight the bubbles on top. Those are two numbers, so the honest way to hit
   * them is to pick the number and let each colour find its own alpha. The
   * alternative — one shared opacity across a palette whose entries differ by
   * three stops of lightness — is what makes half a flock disappear and the
   * other half shout.
   *
   * Monotone in alpha (the composite only ever moves away from the sky), so a
   * bisection converges. Capped below 1 so no grain is ever a solid stamp; a
   * colour too pale to reach the target inside the cap simply comes back at the
   * cap, which is the right failure — it means "do not use this one here".
   */
  private static alphaForContrast( ink: string, sky: string, target: number, cap = 0.92 ): number {
    let lo = 0, hi = cap;
    for ( let k = 0; k < 22; k++ ) {
      const mid = ( lo + hi ) / 2;
      if ( LineChatBgComponent.contrast( mixHex( sky, ink, mid ), sky ) < target ) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    return +( ( lo + hi ) / 2 ).toFixed( 3 );
  }

  /**
   * Istanbul: a flock of GULLS over the Bosphorus.
   *
   * The gull was drawn for Roraima and is REASSIGNED here (owner 2026-08-14) —
   * nothing is more Istanbul than the birds over the strait, and it is the one
   * subject that floats without implying a horizon, which is the trap that
   * killed the flying tepui. There is deliberately no water, no shore and no
   * ferry under them: the moment anything sits below the flock the wallpaper
   * grows a waterline, and LINE centres and CUTS this image, so a waterline
   * would land at a different height on every phone.
   *
   * Structure is the family's: Sakura's jittered grid, one grain per cell, size
   * carrying the depth, tilt kept small because a gull banks a few degrees and
   * does not tumble. Two things are Istanbul's own —
   *  - the size curve is SKEWED (a power on the jitter), so most of the flock is
   *    far off and a few birds are near, which is how gulls over the strait
   *    actually stack up. Roraima's even spread reads as one distance.
   *  - every gull leans the SAME few degrees into the wind coming up the strait,
   *    so the flock shares a sky instead of milling.
   *
   * COLOUR is the hard part here and the reason this is not a recolour of
   * Roraima. That flock is drawn in its sunset palette because browns carried
   * toward the brown skin vanished into it; Istanbul's skin is a very pale cyan
   * (#F2FDFF), so anything pale vanishes exactly the same way — Sea Glass tops
   * out at 1.44:1 even at full opacity, and Turquoise Tile at 1.64:1. So the
   * flock is drawn in the palette's DEEP end and each bird's opacity is solved
   * for a measured contrast rather than picked (see alphaForContrast).
   */
  private buildIstanbul( p: Product ): void {
    // The flat cyan to paste into LINE's talk-room field: the chat body of the
    // skin the owner picked, pixel-sampled off the device (2026-08-14). This
    // component paints NO sky — that colour IS the sky.
    this.skinColor = '#F2FDFF';

    // The deep end of the theme's own palette. Every one of these clears the
    // target band on this sky at a workable opacity; the pale half of the
    // palette cannot and is not here.
    const MARMARA = '#0887B5';
    const TEAL    = '#1190A1';
    const FERRY   = '#397FB7';
    const DEEP    = '#087E8E';
    const IZNIK   = '#12ACC1';
    // Midnight Strait (#083A41) is the palette's darkest and was in this list
    // for two passes. It is out, and the reason generalises: a near-black has to
    // be thinned to about a third of an opacity to stay inside the quiet band on
    // a sky this pale, and thinning kills what little saturation it has — its
    // gulls came back GREY-SAGE while the mid blues beside them at twice the
    // opacity stayed blue. On a very pale sky the colours that survive are
    // MID-luminance and SATURATED, not dark.
    // Warm accents, kept to two of thirteen — one wing catching the late light,
    // not a second palette running through the flock. Golden Horn was a third
    // and is gone: a yellow is the OPPOSITE of a cyan sky, so its two gulls
    // were the loudest marks on the wallpaper at a contrast the blues carry
    // quietly. Contrast is a luminance measurement and hue distance rides on
    // top of it, which is why the warm pair below also aims lower than the
    // blues do (WARM_HANDICAP).
    const TULIP   = '#C16979';
    const COPPER  = '#B87958';
    // Thirteen entries against six columns: coprime, so the cycle never lines a
    // colour up down a column the way a length that divides the grid would.
    // Thirteen stays (prime, so it never lines a colour up down a six-column
    // grid), but COPPER now takes three of the slots instead of one — the owner
    // wanted more of the orange bird in the flock (2026-08-14: "biraz o turuncu
    // martı daha fazla olsun"). Warm is 4 of 13 now against 2 before. Kept under
    // a third: past that the flock stops being gulls over the Bosphorus with the
    // light catching a few of them and becomes two flocks in two colours.
    const tints = [
      MARMARA, TEAL, COPPER, IZNIK, DEEP, TULIP, MARMARA,
      COPPER, DEEP, COPPER, TEAL, IZNIK, FERRY,
    ];
    const WARM_HANDICAP = 0.15;
    const warm = new Set( [ TULIP, COPPER ] );
    /** The contrast this grain should land on: near gulls stand a little
     *  clearer than far ones, and a warm one aims lower for the same presence. */
    const aim = ( fill: string, d: number ): number =>
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.55 + d * 0.60 - ( warm.has( fill ) ? WARM_HANDICAP : 0 ) ).toFixed( 3 ) );

    // ONE knob to reshuffle the whole flock (owner 2026-08-14: "chat background
    // random bi daha üretelim martıları"). Math.random is forbidden here — it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview — so the shuffle is a phase shift on
    // the seeded noise instead. Change SEED and every position, lean, depth and
    // colour moves together, deterministically. It is the same trick the strip
    // uses, and it means "generate another one" costs one number.
    // 0.1187 is the owner's pick, chosen from four rendered candidates
    // (2026-08-14). Do NOT reshuffle this without being asked — the exported
    // wallpaper and the preview have to stay the same picture.
    const SEED = 0.1187;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };
    // Clear margin on every edge: LINE centres and cuts this image, so a gull
    // near an edge is a gull that gets sliced in half on a tall phone.
    const mx = 70, my = 80;
    const cols = 6, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];
    // Three cells left empty, spread across the grid rather than taken off one
    // edge, so the sky has some air in it without opening a bald patch. Five
    // drops on top of a near-full-cell jitter was too many: the first pass tore
    // ten holes the size of a chat bubble into the flock.
    // 29 is the bottom-RIGHT cell, dropped on the owner's call (2026-08-14:
    // "chat background'da sağda alttaki martısı silelim"). Its companion goes
    // with it, which is the point — that corner is where the chat's own input
    // bar and the newest bubbles sit, so it is the one place the flock competes
    // with something.
    const dropped = new Set( [ 9, 16, 26, 29 ] );
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        if ( dropped.has( i ) ) { continue; }
        // Jitter kept to the middle three quarters of the cell. Opened up to
        // almost the whole cell (Roraima's setting) the grid stops doing the one
        // job it is here for, which is even coverage.
        const jx = 0.13 + fr( i * 0.6180339887 + 0.29 ) * 0.74;
        const jy = 0.13 + fr( i * 0.7548776662 + 0.61 ) * 0.74;
        const x = Math.round( mx + ( c + jx ) * cellW );
        const y = Math.round( my + ( r + jy ) * cellH );
        // One wind up the strait: every gull leans the same few degrees, with
        // only a little play around it. A gull rotated far enough stops being a
        // gull, so the whole span is under twenty degrees.
        const rot = Math.round( -4 + ( fr( i * 0.5497004779 + 0.5 ) - 0.5 ) * 22 );
        // Depth, skewed small: the power pushes most of the flock into the
        // distance and leaves a handful near, which is what gives the sky its
        // sense of going back rather than being a plane of stickers.
        const depth = Math.pow( fr( i * 0.3819660112 + 0.2 ), 1.45 );
        const s = +( 0.26 + depth * 0.40 ).toFixed( 3 );
        const fill = tints[ i % tints.length ];
        this.petals.push( {
          d: LineChatBgComponent.GULLS[ i % LineChatBgComponent.GULLS.length ],
          transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ s }) translate(-60,-60)`,
          fill, o: aim( fill, depth ),
        } );
        // Gulls fly in loose pairs and threes. Every third cell gets a
        // companion off at its own angle, and every seventh a third bird
        // further out and smaller still — a one-per-cell scatter reads as
        // wallpaper dots however well it is jittered.
        const wing = ( k: number, mul: number, reach: number, drop: number ) => {
          // Floored: a companion of an already-distant gull went down to 0.11
          // in the first pass, which is a speck and not a bird.
          const s2 = +Math.max( 0.19, s * mul ).toFixed( 3 );
          const a = fr( i * 0.4142 + k * 0.37 ) * Math.PI * 2;
          const f2 = tints[ ( i + k ) % tints.length ];
          this.petals.push( {
            d: LineChatBgComponent.GULLS[ ( i + k ) % LineChatBgComponent.GULLS.length ],
            transform: `translate(${ Math.round( x + Math.cos( a ) * reach * s ) },`
              + `${ Math.round( y + Math.sin( a ) * reach * 0.62 * s ) })`
              + ` rotate(${ rot - 5 }) scale(${ s2 }) translate(-60,-60)`,
            fill: f2, o: aim( f2, Math.max( 0, depth - drop ) ),
          } );
        };
        if ( i % 3 === 2 ) { wing( 4, 0.62 + fr( i * 0.271 ) * 0.2, 120, 0.2 ); }
        if ( i % 7 === 3 ) { wing( 9, 0.5 + fr( i * 0.533 ) * 0.14, 190, 0.3 ); }
      }
    }
  }

  /**
   * Paris: a toile of small EIFFEL TOWERS over the skin's near-white pink.
   *
   * Three motifs were tried and thrown out before this, and the run is worth
   * keeping because each failed for a different reason:
   *  - a drifting bouquiniste PAGE: "kağıt olmaz ne saçma, paris kağıt mı" —
   *    paper is a thing you find in Paris, not a thing that says Paris;
   *  - a CROISSANT: every geometry came out reading as a crown;
   *  - a FLEUR-DE-LYS: drawn well in the end, and still wrong, because a royal
   *    and religious charge repeated across a field reads as a party banner
   *    ("hristiyan parti bayrağı gibi oldu"). That is a register, not a shape,
   *    and no amount of redrawing removes it.
   *
   * The tower has none of those problems. It is the one form here that cannot be
   * mistaken for anything else, it carries no borrowed register, and it is
   * already proven legible at 22px because the Home tab and the passcode dots
   * draw the same contour (line-eiffel-glyph). A wall of small towers is a toile
   * print, which is itself a French idea.
   *
   * They STAND UPRIGHT. Every other wallpaper in this family tumbles its
   * fragment, because a leaf or a bird in the air has no fixed axis — a tower
   * does, and a tilted one reads as a mistake rather than as motion. The variety
   * comes from size and tone instead.
   */
  private buildParis( p: Product ): void {
    // The flat colour to paste into LINE's talk-room field: Paris's chat body,
    // pixel-sampled from the owner's device screenshots (2026-08-14). A near-white
    // pink. This component paints NO sky — that colour IS the sky.
    this.skinColor = '#FFF1F2';

    // ONE knob for "generate another one". Math.random is forbidden — it would
    // hand back a different wallpaper on every rebuild and the exported asset
    // would stop matching the preview.
    const SEED = 0.5738;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // PINKS ONLY and light, the owner's call on the previous motif and just as
    // right here: the wine and gold end of this palette turns a wall of towers
    // into a poster. These are the rose family top to bottom, so the variation
    // reads as distance rather than as a second colour scheme.
    // Pinks carry it, with SEINE BLUE and EIFFEL GOLD back as the two accents
    // (owner 2026-08-14). They were taken out when the motif was a fleur-de-lys,
    // where four temperatures across a heraldic repeat read as a muddle; a toile
    // of towers is a looser thing and the two cool/warm notes give it a night.
    // Nine entries against five columns, coprime, so no colour ever lines up
    // down a column.
    const TINTS = [ '#DF7583', '#D584A3', '#5E7BB3', '#C79FAE', '#D5A24C',
                    '#DF7583', '#F3B7A9', '#5E7BB3', '#D5A24C' ];
    const aim = ( fill: string, d: number ): number =>
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.18 + d * 0.34 ).toFixed( 3 ) );

    const mx = 58, my = 58;
    // Density is set by COUNT, never by size — the towers were taken down to a
    // print scale and growing them back to fill the field would undo that.
    // Twenty-five, on a 5x5, with TIGHT margins. Both came from measuring the exported
    // PNG rather than the model: at a 110/120 margin the top quarter of the
    // canvas came out bare (0.00, 0.00, 0.88, 0.00 percent ink across its four
    // cells) and at 90/90 the two top CORNERS were still empty. Points can be
    // perfectly evenly spaced and still leave a band or a corner with nothing in
    // it — a margin plus a run of jitter landing low in its row is enough — and
    // no model that only looks at spacing can see that.
    //
    // This is the third layout in this file to end up searched instead of tuned.
    // Eyeballing a scatter reliably fixes the patch you are looking at and opens
    // another one somewhere else.
    const cols = 5, rows = 5;
    const dropped = new Set<number>( [] );
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        if ( dropped.has( i ) ) { continue; }
        const jx = 0.03 + fr( i * 0.6180339887 + 0.29 ) * 0.94;
        const jy = 0.03 + fr( i * 0.7548776662 + 0.61 ) * 0.94;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          s: +( 0.44 + Math.pow( fr( i * 0.3819660112 + 0.2 ), 1.2 ) * 0.16 ).toFixed( 3 ),
        } );
      }
    }
    // Relaxed apart, the layout Roraima's fronds settled on: plain jitter leaves
    // bare patches that no amount of seed hunting fixes.
    //
    // Four passes, and the layout around them was found by measuring the RENDER
    // rather than the model — which is the lesson worth keeping here.
    //
    // Scoring the point positions said this scatter was even at every setting
    // tried. Measuring the exported PNG said the top quarter of the canvas was
    // BARE: 0.00, 0.00, 0.88, 0.00 percent ink across its four cells. Points can
    // be evenly spaced and still leave a band empty, because a margin plus a run
    // of jitter that happens to land low in its row pushes every tower out of
    // it, and a model that only looks at spacing cannot see that.
    //
    // So the margins came in (110/120 to 90/90) and the search scores coverage
    // over the whole canvas in a 4x4, weighted by each tower's own reach. Weakest
    // cell 0.112 against a mean of 0.19, where the hand-set version left cells at
    // zero.
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.06;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.2 ), 1.2 );
      const fill = TINTS[ i2 % TINTS.length ];
      // A LITTLE lean, +-15 degrees (owner 2026-08-14). Bounded tight for the
      // reason the upright rule was there in the first place: a tower has an
      // axis, and past about 20 degrees it stops reading as a tower standing and
      // starts reading as one falling over. Fifteen is enough to break the print
      // out of a grid without touching that.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.5 ) - 0.5 ) * 30 );
      this.petals.push( {
        d: EIFFEL_SOLID,
        transform: `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
          + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-60)`,
        fill, o: aim( fill, depth ),
      } );
    }
  }


  /**
   * Rio: PLAJ ŞEMSİYELERİ — beach parasols — scattered over the skin's grey.
   *
   * The third asset to take this object, after the Home tab and the passcode dots
   * (owner 2026-08-14: "şimdi chat background. ya orda da şemsiyeler ya da başka
   * bişey"), and taking it is what makes the theme read as one thing: a bar
   * glyph, a lock screen and a wallpaper all showing the same parasol is how
   * Sakura works with its blossom and Paris with its tower.
   *
   * PIPAS — Brazilian kites — held this wallpaper before it and the module is
   * still in the tree (line-pipa-glyph). They were the better answer while the
   * hero was Cristo Redentor, because a statue and a kite share nothing and the
   * wallpaper had to carry its own subject; once the parasol took both other
   * assets, a field of kites was simply a fourth idea in a theme that already had
   * three.
   *
   * What carries it:
   *  - They STAND, and they lean. A parasol has an upright and a lean off it, so
   *    unlike a drifting petal or a tumbling kite the angle is bounded — ±26°,
   *    about the point the pole enters the sand, never mirrored into a fall.
   *  - TWO COLOURS per parasol, alternating panels — but here the PAIR CHANGES
   *    from parasol to parasol (owner 2026-08-14: "şemsiye renkleri farklı
   *    ikililer olsun mavi sarı mavi yeşil sarı yeşil falan"), where the tab and
   *    the dots hold one fixed green-and-yellow. A beach is a row of different
   *    parasols; a tab bar is one object, and the two should not be the same rule.
   *  - Within each pair the DARKER colour takes the outer panels and the pole.
   *    That is measured per pair rather than fixed, because the pairs mix blues,
   *    greens and yellows and which one carries the edge changes with them — a
   *    canopy edged in the lighter of the two has no edge on this grey.
   *  - The pole is drawn in the green too, never in a third tone: at wallpaper
   *    scale a differently-coloured stick reads as a crack in the shape.
   */
  private buildRio( p: Product ): void {
    // The flat GREY to paste into LINE's talk-room field, sampled from the owner's
    // device (2026-08-14 21.23). This component paints NO sky — that colour IS the
    // sky, and every tint below is aimed at a neutral grey rather than at a green.
    this.skinColor = '#ECECEC';

    // ONE knob for "generate another one". Math.random is forbidden — it would
    // hand back a different wallpaper on every rebuild and the exported asset
    // would stop matching the preview.
    const SEED = 0.3271;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The pairs, and they come from the theme's COLOUR CIRCLES rather than from
    // its wide palette (owner 2026-08-14: "color circle'daki koyu yeşil mavi ve
    // sarının kombinasyonlarını yapacaksın"). Three circles — Emerald Palm,
    // Guanabara Blue, Ipanema Sun — and the two pairs that keep the yellow.
    //
    // TWO of the three, not all three (owner, a minute later: "aslında mavi sarı
    // ve yeşil sarı sadece"). Green + blue is out, and it is the one that deserved
    // to go: both halves are dark and cool, so it was the only pair whose two
    // colours did not separate at wallpaper scale — it read as one navy-green
    // canopy where the point is that every parasol is visibly striped. The yellow
    // is what makes a pair legible here, so both surviving pairs contain it.
    //
    // Circles, not palette, is a rule this family has already paid for: Miami's
    // passcode first took Deep Lime, which lives in the wide palette, and the
    // owner's correction was the same one — the five circles are what the site
    // and the store card show, so they are what the assets have to be built from.
    //
    // An earlier set had six pairs including Sunset Red + Carnival Gold, and the
    // owner cut the warm one outright ("turuncu yok onu sil"): the two together
    // average to an orange the theme does not have.
    const PAIRS: [ string, string ][] = [
      [ '#2287D5', '#ECDA61' ],   // Guanabara Blue + Ipanema Sun
      [ '#13A166', '#ECDA61' ],   // Emerald Palm + Ipanema Sun
    ];
    /** Relative luminance, for deciding which half of a pair carries the edge. */
    const lum = ( hex: string ): number => {
      const n = hex.replace( '#', '' );
      const ch = [ 0, 2, 4 ].map( ( k ) => {
        const v = parseInt( n.slice( k, k + 2 ), 16 ) / 255;
        return v <= 0.04045 ? v / 12.92 : Math.pow( ( v + 0.055 ) / 1.055, 2.4 );
      } );
      return 0.2126 * ch[ 0 ] + 0.7152 * ch[ 1 ] + 0.0722 * ch[ 2 ];
    };

    // STRONGER than the wallpapers this file usually draws, and the reason is the
    // two-colour rule rather than taste. At the 1.22-1.60 band the other themes
    // use, both halves of a pair land within a few percent of the grey and of each
    // other, and a parasol that is meant to be blue AND yellow arrives as one
    // washed blob (owner 2026-08-14: "ikili dedim passcode'daki gibi her şemsiye 2
    // renk içerecek" — said of a build where the pairs were already there and
    // simply could not be seen).
    //
    // The LIGHTER half is aimed higher than the darker one, because on this grey
    // it is the half that disappears first: Carnival Gold needs most of its alpha
    // before it is a colour at all, while Deep Jungle is legible at half of it.
    const aim = ( fill: string, d: number, light = false ): number =>
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( ( light ? 1.42 : 1.75 ) + d * 0.55 ).toFixed( 3 ) );

    // Paris's layout, and for its reason: a jittered grid relaxed apart, because
    // plain jitter leaves bare patches no seed hunt fixes, and because coverage
    // has to be measured on the RENDER rather than on the point set — points can
    // be evenly spaced and still leave a whole band of canvas empty.
    const mx = 66, my = 70;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.04 + fr( i * 0.6180339887 + 0.31 ) * 0.92;
        const jy = 0.04 + fr( i * 0.7548776662 + 0.57 ) * 0.92;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          s: +( 0.50 + Math.pow( fr( i * 0.3819660112 + 0.22 ), 1.2 ) * 0.20 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.04;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.22 ), 1.2 );
      // ±26 degrees, about the pole's foot. Bounded tighter than the kites' ±34
      // for the reason Paris's towers are: a parasol has an upright, and past
      // about thirty degrees it stops reading as planted and starts reading as
      // blown over.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.5 ) - 0.5 ) * 52 );
      const at = `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
        + ` rotate(${ rot }) scale(${ pt.s })`
        + ` translate(${ -PARASOL_PIVOT[ 0 ] },${ -PARASOL_PIVOT[ 1 ] })`;
      // The pair for this one, darker half first: it takes the outer panels and
      // the pole, so the shape's edge is always the stronger of the two.
      const pair = PAIRS[ i2 % PAIRS.length ];
      const dark = lum( pair[ 0 ] ) <= lum( pair[ 1 ] ) ? pair[ 0 ] : pair[ 1 ];
      const light = dark === pair[ 0 ] ? pair[ 1 ] : pair[ 0 ];
      // Inner panels first, then the outer ones over them, then the pole — the
      // same draw order the tab and the dots use.
      this.petals.push( {
        d: PARASOL_PANELS_B, transform: at,
        fill: light, o: aim( light, depth, true ),
      } );
      this.petals.push( {
        d: PARASOL_PANELS_A, transform: at,
        fill: dark, o: aim( dark, depth ),
      } );
      this.petals.push( {
        d: PARASOL_POLE, transform: at,
        fill: dark, o: aim( dark, Math.max( 0, depth - 0.12 ) ),
      } );
    }
  }


  /**
   * Tallinn: KAR TANELERİ — snowflakes drifting over the talk room (owner
   * 2026-08-14). The same six-armed flake the Home tab and the passcode dots
   * draw, so all three of this theme's assets are one object.
   *
   * Snow is the one motif in this family that needs no excuse to be in the air,
   * which is the test Roraima's three rejected wallpapers failed — and it is also
   * the only one with no fixed axis, so unlike Paris's towers or Rio's parasols
   * these are free to sit at any angle at all. They still get one: a hexagonal
   * flake repeats every 60°, so the tumble is taken modulo that and what varies
   * is really size and tone.
   *
   * TWO GEOMETRIES, by size. The full flake carries two stations of branches and
   * the small one carries a single fatter station: below about a third of the box
   * the outer branches land under a pixel and fill in, and a flake that has
   * closed into a disc is worse than one drawn with fewer branches.
   *
   * The hard part here is not the drawing, it is that this skin's talk room is a
   * near-WHITE cyan. White snow on it is nothing at all, so these are drawn in the
   * theme's own cold blues and the alpha is measured rather than picked.
   */
  private buildTallinn( p: Product ): void {
    // The skin's own talk room, sampled from the owner's device (2026-08-14
    // 22.06): a pale winter blue. This component paints NO sky — that colour IS
    // the sky, and every tint below is aimed at it.
    this.skinColor = '#D3EAF2';

    // ONE knob for "generate another one". Math.random is forbidden — it would
    // hand back a different wallpaper on every rebuild and the exported asset
    // would stop matching the preview.
    const SEED = 0.6142;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // Mostly WHITE, with the theme's cold blues behind it. The sky here is already
    // a pale blue, so white flakes are the near ones and the blues are the ones
    // further back — snow at two distances rather than one flat scatter. Nothing
    // warm: a red-roof tint among falling snow reads as dirt, and the roofs have
    // the menu strip to themselves.
    const TINTS = [ '#FFFFFF', '#A9B9DA', '#FFFFFF', '#7FB6D9', '#FFFFFF',
                    '#8FA3C9', '#FFFFFF' ];
    const aim = ( fill: string, d: number ): number =>
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.20 + d * 0.40 ).toFixed( 3 ) );

    // Sakura's structure — a jittered grid, relaxed apart — because coverage has
    // to be even without anything sitting on a grid line, and because plain
    // jitter leaves bare patches no seed hunt fixes.
    const mx = 60, my = 62;
    const cols = 5, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.04 + fr( i * 0.6180339887 + 0.19 ) * 0.92;
        const jy = 0.04 + fr( i * 0.7548776662 + 0.43 ) * 0.92;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // A wide size range, wider than the other wallpapers here: snow falls at
          // every distance at once, and a field of one-sized flakes reads as a
          // pattern rather than as weather.
          s: +( 0.20 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.5 ) * 0.34 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.5 );
      const fill = TINTS[ i2 % TINTS.length ];
      // Rotation is taken modulo 60 degrees, since the flake repeats there — past
      // that the "different angle" is the same picture and the seed is wasted.
      const rot = Math.round( fr( i2 * 0.5497004779 + 0.31 ) * 60 );
      this.petals.push( {
        d: pt.s < 0.30 ? SNOWFLAKE_SIMPLE : SNOWFLAKE,
        transform: `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
          + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-60)`,
        fill, o: aim( fill, depth ),
      } );
    }
  }

  /**
   * Sequoia: sprigs of redwood foliage drifting over the chat skin — the same
   * scattered structure Sakura's petals use, with the theme's own fragment.
   * Sakura scatters petals off its blossom, Tokyo stars out of its sky, Sequoia
   * needles off the branch its icon draws.
   *
   * Drawn as STROKES, not fills: a needle is a line, and filling these turns the
   * sprig into a blob at wallpaper scale.
   */
  /**
   * OCEAN: a drift of jellyfish over the talk room, with their bubbles going up
   * between them.
   *
   * The same animal the Home tab and the four passcode dots draw — the shape this
   * family likes best, and the reason a theme reads as one thing rather than as
   * three unrelated drawings.
   *
   * Two things about this one are its own.
   *
   * It is the first DARK wallpaper since Roraima's, and that turned out to cut
   * the OPPOSITE way from the one this was first written for. The reasoning went:
   * these are light marks on deep water, so hold them down or they will glow —
   * and the targets went to 1.16..1.44, below anything else in the family. What
   * came back was an empty rectangle. On a sky this dark (#0E2B30, relative
   * luminance 0.023) a contrast of 1.16 is an alpha of 0.068, which is not a
   * faint jellyfish, it is no jellyfish.
   *
   * 1.55..2.25 is what a mark on this ground actually costs: alpha 0.19 at the
   * back to 0.35 at the front. Rio's light skin runs 1.75..2.30 for the same
   * apparent weight, which is the useful way to read this — a dark sky needs
   * roughly the same NUMBER, not a smaller one, and the instinct to go quieter on
   * a dark ground is simply wrong.
   *
   * And the BUBBLES are not decoration. A field of jellyfish alone reads as a
   * pattern of one shape at several sizes; the bubbles give it a second, much
   * smaller element and a direction, which is what turns a pattern into water.
   */
  /**
   * SALDA: shells washed up across the talk room, with pebbles between them.
   *
   * The same scallop the Home tab and the four passcode dots draw — the shape
   * this family likes best, and what makes a theme read as one thing rather than
   * as three unrelated drawings.
   *
   * It is Ocean's wallpaper inverted in every way, which is a useful thing to be
   * able to say out loud: dark marks on a pale ground rather than light on deep,
   * and a SECOND element that lies still (pebbles) rather than one that rises
   * (bubbles). The second element is not decoration in either case. A field of
   * one shape at several sizes is a pattern; give it a much smaller companion and
   * it becomes a surface.
   */
  /**
   * PRAGUE: lit street lamps scattered across the night talk room.
   *
   * THESE REPLACED GOTHIC WINDOWS. The first pass scattered lancets — a piece of
   * the cathedral the Home tab used to draw — and when the cathedral went for
   * reading as a religious emblem, its windows had to go with it. A pointed arch
   * with tracery in it is a church window wherever you put it.
   *
   * The lamp is the better grain anyway, and for the very reason the lancet was
   * chosen in the first place: it is ONE MARK. A whole lamp is small enough to
   * repeat where a whole cathedral was not, so this theme no longer needs a
   * fragment at all — the wallpaper and the tab draw the same object, which is
   * what most of this family does.
   *
   * It is still the only wallpaper in the run that is LIT rather than tinted:
   * every lamp carries a pale core in its panes, so the field reads as lamps
   * burning rather than as lamp-shaped confetti.
   */
  /**
   * SYDNEY: sails scattered across the harbour, with the water's own ripples
   * between them.
   *
   * ONE shell, not the building — a piece of the hero object rather than a copy
   * of it, the relationship Sakura's petals have with its blossom and Prague's
   * lancets with its cathedral. Here the reason is a register rather than a
   * geometry: twenty Opera Houses behind a chat is a postcard rack, while twenty
   * sails is boats on a harbour, which is the same harbour the strip draws.
   *
   * The RIPPLES are the second element every wallpaper in this run has, and they
   * are the flattest one — Ocean's bubbles rise, Salda's pebbles lie still, these
   * lie still AND lie down. That is what water does at a distance, and it is what
   * the theme's own icon covers its harbour with.
   */
  /**
   * REYNISFJARA: a colonnade of basalt columns standing across the talk room.
   *
   * The beach's OTHER form of the same rock. Everywhere else in this run the
   * wallpaper is either the hero object repeated (Ocean's jellyfish, Tallinn's
   * flake) or a fragment of it (Prague's lancets, Sydney's sail); this is neither.
   * The stacks offshore and the cliff behind the sand are two different things
   * made of one material, cooled from one lava, and drawing one on the tab bar and
   * the other on the wallpaper is the closest this family gets to a theme having
   * two subjects without having two subjects.
   *
   * The columns DO NOT ROTATE and they are not scattered on a grid the way every
   * other wallpaper here is. They stand in short ROWS with their feet on a common
   * line, because that is what a colonnade is — a field of independently floating
   * columns is a field of posts.
   */
  /**
   * SPRING: bellflowers across the talk room, with buds between them.
   *
   * The same flower the Home tab and the four passcode dots draw. It replaced a
   * flock of hummingbirds, which went for being careless — a bird in flight has no
   * stable silhouette, and twenty of them at wallpaper scale was twenty darts.
   *
   * Its second element is the only one in this run that is the SAME PLANT at
   * another stage: a bud is a bell that has not opened. Ocean's bubbles, Salda's
   * pebbles and Sydney's ripples are things that happen to be near the hero;
   * these are the hero, earlier.
   */
  /**
   * VIENNA: f-holes across the talk room, with note heads between them.
   *
   * The most economical fragment in this run. Prague's lancet is a window off a
   * cathedral and Sydney's sail is one shell off a building, but both are still
   * parts of a structure; an f-hole is a hole. It names the instrument it was cut
   * out of, at any size, and belongs to nothing else in the world.
   *
   * They DO NOT ROTATE. An f-hole has an up — the two eyes are different sizes and
   * the S leans one way — and turned it stops being an f and becomes a squiggle.
   * Prague's windows are the only other grain in this run held upright, and for
   * exactly the same reason.
   */
  /**
   * MADRID: cups of chocolate across the talk room, with loose churros between.
   *
   * It replaced fans and polka dots, which went with the flamenco motif — a
   * Spanish subject rather than a Madrid one.
   *
   * The second element is a single STICK, which makes this the tidiest pairing in
   * the run: the grain is the whole order and the companion is one item off it.
   * Ocean's bubbles happen to be near its jellyfish and Sydney's ripples near its
   * sails; a churro is IN the cup.
   */
  /**
   * ETNA: embers drifting up the talk room, with ash between them.
   *
   * A piece of the hero rather than a copy of it — Sakura's relationship, and the
   * only one in this run where the fragment is something the hero THREW rather
   * than something it is made of. Prague's lancets are cut into its cathedral and
   * Sydney's sail is one shell of its building; an ember has left.
   *
   * That is also why this is the only wallpaper here whose grain gets SMALLER and
   * FAINTER toward the top of the canvas rather than being scattered evenly. An
   * ember cools as it rises, and a field of them at one brightness is a starfield.
   */
  /**
   * FUJI: the mountain scattered across the talk room, with cloud bars between.
   *
   * The only wallpaper in this run that draws a DIFFERENT SUBJECT from its own tab
   * glyph. Everywhere else the wallpaper is the hero repeated (Ocean, Salda,
   * Tallinn), a piece of it (Prague, Sydney, Vienna, Etna) or the same material in
   * another form (Reynisfjara). Fuji has two subjects because its icon has two,
   * standing one behind the other — the pagoda in front and the mountain behind —
   * and the wallpaper is simply the far one.
   *
   * There is no cherry blossom anywhere in it, which is the loudest absence in the
   * whole run and the most deliberate: Sakura is a theme in this same set and the
   * blossom is its entire identity.
   */
  private buildFuji( p: Product ): void {
    // The skin's own talk room (see line-color-set's FUJI), SAMPLED: the blush. The
    // guess had #FFF6F2, a near-white; the owner went a good deal pinker, and every
    // tint below now sits on a ground about 10% darker than it was solved against.
    this.skinColor = '#FFE8E1';

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.3856;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // A dawn, so the tints are a SEQUENCE rather than a family: slate before the
    // sun, wisteria, teal, then the rose and vermilion at it.
    const TINTS = [ '#5F7D9F', '#BF8AB3', '#39A1A5', '#DD747A', '#5F7D9F',
                    '#D37154', '#BF8AB3' ];
    const aim = ( fill: string, d: number ): number =>
      // LOWERED from 1.26..1.70. A contrast window is read against the ground it
      // sits on, and this ground came in darker than the one these numbers were
      // set for: at the old window the mist came back heavier than a wallpaper
      // should be. The blush is still the palest room in the family, so the window
      // stays the lowest here — Reynisfjara's near-black runs at 2.4.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.18 + d * 0.40 ).toFixed( 3 ) );

    const mx = 82, my = 86;
    // MORE of them, now that they are the only thing here. Sixteen was a count set
    // when fourteen cloud bars were sharing the room; alone, sixteen mountains on a
    // canvas this size is a handful.
    const cols = 5, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          s: +( 0.34 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.34 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // A SMALL turn, either way (owner 2026-08-17). It used to be pinned upright on
      // the argument that this is a specific mountain and a tilted Fuji is not Fuji
      // — true of a photograph and not of a grain: sixteen identical uprights read
      // as a stamp repeated, and a few degrees of variation is what makes a scatter
      // look scattered. It stays SMALL for the original reason, which was sound.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 18 );
      const place = `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
        + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-70)`;
      this.petals.push( { d: FUJI_MOUNT, transform: place, fill, o: aim( fill, depth ) } );
      // the snowcap, in the SKIN's own colour rather than in white — it reads as the
      // mountain's rock being cut away rather than as a white hat sitting on it
      this.petals.push( {
        d: FUJI_SNOW, transform: place,
        fill: this.skinColor, o: +( 0.62 + depth * 0.25 ).toFixed( 3 ),
      } );
    }

    // NO CLOUD BARS (owner 2026-08-17: "hem bulut hem dağ var, olmaz — sadece dağ
    // olacak").
    //
    // They were the print's own device and there were fourteen of them, big and
    // horizontal. Two problems, and the owner named the first: a wallpaper carrying
    // two subjects at two scales asks the eye to hold both, and this one is a chat
    // background. The second is that at phone size a long thin horizontal bar in a
    // pale tint does not read as a cloud at all — it reads as a line of text behind
    // the messages, which is the worst thing a chat wallpaper can look like.

  }

  private buildEtna( p: Product ): void {
    // The skin's own talk room (see line-color-set's ETNA), SAMPLED: LINE's stock
    // near-black. One unit off the #121111 that was derived.
    this.skinColor = '#131313';

    // ONE knob for "generate another one". Math.random is forbidden here: it would
    // hand back a different wallpaper on every rebuild and the exported asset would
    // stop matching the preview.
    const SEED = 0.9042;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    /*
     * EMBERS, drawn as LIGHT.
     *
     * SIX designs were tried on this wallpaper before this one and all six were
     * rejected: a four-pointed ember (read as confetti), a teardrop ember (rain), a
     * bare volcano cone (a traffic cone), the cone with puffed smoke (a chess pawn,
     * then a chain of beads), the cone with a ribbon plume, and a network of glowing
     * fissures (ugly — the owner's word, and fair).
     *
     * The pattern in those six is not that the subjects were wrong. Five of them
     * were hard-edged OBJECTS, which at a fortieth of their drawn size have to pass
     * a "what is this?" test that the scale itself destroys the answer to. The
     * sixth was a texture, which passed that test and lost on looks — a wallpaper
     * sits behind someone's messages all day, so quiet and pretty is the actual
     * brief, and a lattice of bright cracks is neither.
     *
     * Every wallpaper that has worked in this family is small, soft and sparse:
     * Sakura's petals, Tallinn's snowflakes, Spring's tulips, Vienna's notes. The
     * ember concept was right twice over and the DRAWING was what failed — because
     * an ember is not an outline, it is a point of light. Drawn as a blurred halo
     * with a small core it cannot be mistaken for confetti or for rain, since it
     * has no edge to give it a shape.
     */
    const TINTS = [ '#F1735F', '#E67748', '#D74C57', '#F09A3E', '#C66439' ];
    /** The core is always hotter than the halo around it. */
    const CORES = [ '#FFC98A', '#FFB870', '#FFA36A', '#FFD79A', '#FF9E5E' ];

    this.petals = [];
    this.embers = [];

    // FIFTY-FOUR, on a jittered grid so the field is even without being regular. A
    // scatter from a bare random function clumps, and a clump of glows on black is
    // a light leak.
    //
    // SIX columns against FIVE tints, deliberately coprime. The first pass used five
    // of each and every column came out one colour — a stack of identical glows
    // running down the canvas, which is the most visible thing in the whole image
    // and the least intended. Any grid whose column count shares a factor with the
    // palette length does this.
    const COLS = 6, ROWS = 9;
    const cw2 = this.W / COLS, ch2 = this.H / ROWS;
    let k = 0;
    for ( let r = 0; r < ROWS; r++ ) {
      for ( let c = 0; c < COLS; c++, k++ ) {
        // wide jitter, and the odd rows offset half a cell — a lattice you can see
        // is a lattice, and glows are big enough that column alignment shows even
        // through the jitter
        const cx = ( ( c + 0.06 + fr( k * 0.6180339887 + 0.19 ) * 0.88
          + ( r % 2 ) * 0.5 ) % COLS ) * cw2;
        const cy = ( r + 0.06 + fr( k * 0.7548776662 + 0.43 ) * 0.88 ) * ch2;
        // A WIDE size range, wider than any other grain in this family gets. Real
        // embers vary from a speck to a coal, and the variation is most of what
        // makes a field of them look like air rather than like a pattern.
        const t = Math.pow( fr( k * 0.3819660112 + 0.27 ), 1.6 );
        const halo = 14 + t * 40;
        // the tint is hashed off the position rather than off k, so neither the
        // rows nor the columns can carry a colour
        const i = Math.floor( fr( k * 0.4142 + 0.71 ) * TINTS.length ) % TINTS.length;
        this.embers.push( {
          cx: +cx.toFixed( 1 ), cy: +cy.toFixed( 1 ),
          halo: +halo.toFixed( 1 ),
          // the core is a fraction of the halo and never more than a few units: a
          // core that grows with the halo turns the big sparks into balloons
          core: +( 2.2 + t * 3.4 ).toFixed( 1 ),
          fill: TINTS[ i ], coreFill: CORES[ i ],
          // The halo is FAINT and the core is not. That difference is the entire
          // effect: an even opacity across both gives a flat disc, and a disc is a
          // shape again — which is what this wallpaper has failed on six times.
          ho: +( 0.10 + t * 0.20 ).toFixed( 3 ),
          co: +( 0.42 + t * 0.40 ).toFixed( 3 ),
        } );
      }
    }
  }

  private buildMadrid( p: Product ): void {
    // The skin's own talk room (see line-color-set's MADRID), SAMPLED: the one
    // saturated saffron this whole skin is built from, not the warm cream every
    // value here was solved against.
    this.skinColor = '#FFDD66';

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.6604;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The theme's reds and ochres. The reds lead — a wallpaper of ochre fans on a
    // cream ground is a wallpaper of nothing.
    // The reds and browns. The bright gold #DCA225 had to go: it measured 2.09
    // against the old cream and measures 1.71 against this saffron, which is under
    // the top of the window below — it would have clamped at full alpha and still
    // come up short, which is a mark that gets darker without ever getting
    // clearer. On a yellow ground the only quiet colours left are warm browns.
    const TINTS = [ '#B84440', '#A85E2C', '#D37969', '#902926', '#8C2E2B',
                    '#A57F02', '#BD503F' ];
    const aim = ( fill: string, d: number ): number =>
      // LOWERED from 1.58..2.30. The window was set when this room was the palest
      // ground in the run; the sampled saffron is a good deal darker than that
      // cream, and a contrast window is read against the ground it sits on, not
      // chosen once for a theme (see the same move on Vienna and Ocean).
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.42 + d * 0.62 ).toFixed( 3 ) );

    const mx = 80, my = 84;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // NEARLY DOUBLE the first pass's 0.28..0.58, which put a 66px cup on a
          // 1482px canvas — small enough that the ridges closed up and the whole
          // scatter read as crumbs rather than as a repeat.
          s: +( 0.52 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.46 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // THE CLAVEL, and nothing else. The room was cups of chocolate with loose
      // churros scattered between them — two elements, the second one there mostly
      // because a field of cups was monotonous.
      //
      // A flower does not need a second element: its own fringe is the variation.
      //
      // It turns a little more than most things here — sixteen degrees either way.
      // A cut carnation lies at whatever angle it fell, unlike the tulips in Spring
      // which are growing and therefore upright.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 32 );
      // MIRRORED on alternate grains: the head leans off its own stem, so mirroring
      // varies the field without adding to the turn.
      const flip = i2 % 2 ? -1 : 1;
      this.petals.push( {
        d: pt.s < 0.58 ? CLAVEL_SIMPLE : CLAVEL,
        transform: `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
          + ` rotate(${ rot }) scale(${ ( pt.s * flip ).toFixed( 3 ) },${ pt.s })`
          + ` translate(-60,-70)`,
        fill, o: aim( fill, depth ),
      } );
    }
  }

  private buildVienna( p: Product ): void {
    // The skin's own talk room (see line-color-set's VIENNA), SAMPLED: a warm
    // blush, not the auditorium dark every value here used to be solved for.
    this.skinColor = '#F8EDE7';

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.1723;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The theme's golds, its rose and its one green. The golds lead, because the
    // fragment is a hole in a varnished instrument and the light in this room is
    // gold.
    // The varnish golds, the rose and the one green. On the dark room the cream
    // #EACF8C led; on this one it measures 1.32 against the ground and cannot
    // reach the contrast window at any alpha, so the pale end of the set is gone
    // and the warm woods lead instead. Rose and sage stay — they are the two the
    // owner's own screenshot shows reading clearly on this blush.
    const TINTS = [ '#B8722A', '#D7999F', '#C9822B', '#98A67D', '#A85E2C',
                    '#C4848B', '#8A4B2E' ];
    const aim = ( fill: string, d: number ): number =>
      // LOWERED from 2.10..3.40. That window was set for a near-black room; the
      // contrast a wallpaper wants scales with how dark its ground is, and this
      // one is pale enough that anything above about 2 stops being a wallpaper
      // and starts being a pattern you read instead of the messages.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.50 + d * 0.55 ).toFixed( 3 ) );

    const mx = 80, my = 84;
    // MORE and SMALLER than the violins were. A note is a small mark to begin
    // with, so a field of twenty at violin scale reads as a sparse handful; thirty
    // at two thirds of it reads as a page of music.
    const cols = 6, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          s: +( 0.26 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.22 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // NOTES ONLY — no violins (owner 2026-08-16: "chat background sadece notalar
      // olsun kemanlar yerine").
      //
      // The instrument is still the theme's hero: it is the Home tab and it is all
      // four passcode dots. What it was not is a good WALLPAPER grain. A violin is
      // a detailed silhouette with a long thin neck, and a field of them at a
      // fortieth of their drawn size is a field of dark specks with hairs on;
      // the notes read at any size because a note is already a small mark.
      //
      // It also stops the theme repeating one object on every surface, which is
      // what the menu strip's own redesign was for — the strip is a staff, and a
      // wallpaper of loose notes is the same sheet with the ruling taken away.
      this.petals.push( {
        d: NOTE,
        // Barely turned. A note is UPRIGHT on a stave, and a tilted stem does not
        // read as handwriting, it reads as broken.
        transform: `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
          + ` rotate(${ Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 14 ) })`
          + ` scale(${ pt.s.toFixed( 3 ) }) translate(-60,-60)`,
        fill, o: aim( fill, depth ),
      } );
    }
  }

  private buildSpring( p: Product ): void {
    // The skin's own talk room (see line-color-set's SPRING). This component
    // paints NO sky — that colour IS the meadow light, and every tint below aims
    // at it.
    this.skinColor = '#FFFFFF';   // SAMPLED: a plain white talk room, not the pale meadow

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.8317;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The theme's own accents. The roses lead, because the bird is a rose bird and
    // a wallpaper of green birds on a green ground is no wallpaper at all.
    // The passcode's own four, plus two lighter members so the field has depth.
    // NO GREEN: with the foliage fixed green everywhere else, a green CUP reads as
    // a tulip that has not opened rather than as a colour — the same reason the
    // second passcode digit went orange.
    const TINTS = [ '#A87FD0', '#C86A1C', '#4A72B8', '#A34D5F',
                    '#C79BE0', '#DC8395' ];
    const aim = ( fill: string, d: number ): number =>
      // RAISED from 1.28..1.74 — see buildPrague. This is the palest talk room in
      // the run and it had the lowest window in the run, which is how it came back
      // reading as lint on blank paper.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.58 + d * 0.76 ).toFixed( 3 ) );

    const mx = 80, my = 84;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // The range moved twice. It was set for the whole flower (about 100
          // units tall), then tripled when the grain became a leaf — and tripled
          // was too far: the owner's read was that the leaves came out big AND
          // cheap. The new leaf is drawn 40 long with a real point on it, so it
          // needs far less scale to be a leaf.
          // The whole plant is about 70 by 96 in its box against the cup's 50 by
          // 52, so it needs less scale again to sit at the same weight. This range
          // has now moved three times, once per change of grain.
          s: +( 0.30 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.22 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // Half the plants MIRROR rather than turn. A tulip has an up, so rotating
      // it far reads as one that has been trodden on; mirroring only swaps which
      // side its leaves fall to, which is what half a real bed does anyway.
      const flip = i2 % 2 ? -1 : 1;
      const tilt = Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 20 );
      const place = `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
        + ` rotate(${ tilt }) scale(${ ( pt.s * flip ).toFixed( 3 ) },${ pt.s })`
        + ` translate(-60,-64)`;    // the whole plant's own centre in the 120 box
      // THE WHOLE PLANT, in TWO paths — foliage in the theme's fixed green, cup in
      // the grain's own tint. The wallpaper drew the CUP ALONE for one pass and the
      // owner's read was immediate: "lale kafasıysa da bari gövdesi de olsun."
      // A cup with nothing under it is not a tulip at this size, it is a crown.
      //
      // Two entries rather than one because the fill is per path. One flat tulip
      // in a single colour is precisely the version whose leaves follow its bloom,
      // which is the thing FLOWER_GREEN exists to prevent.
      this.petals.push( {
        d: FLOWER_FOLIAGE_JOINED,
        transform: place,
        fill: FLOWER_GREEN, o: aim( FLOWER_GREEN, depth ),
      } );
      this.petals.push( {
        d: FLOWER_HEAD,
        transform: place,
        fill, o: aim( fill, depth ),
      } );
    }

    // NO BUDS (owner 2026-08-16: "o damla gibi olan şeyleri sil"). A scatter of
    // small closed buds drifted between the grains, and at wallpaper size a bud is
    // a teardrop — it read as rain on a meadow rather than as part of the plant.
    // One grain, and it is the hero ([[line-wallpaper-rules]]).
  }

  private buildReynisfjara( p: Product ): void {
    // The skin's own talk room (see line-color-set's REYNISFJARA). This component
    // paints NO sky — that colour IS the dark, and every tint below aims at it.
    this.skinColor = '#131313';   // SAMPLED: LINE's near-black, not the derived Nordic dark

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.4460;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The theme's own greys and its one warm family. Nothing saturated exists in
    // this palette to reach for, which is the whole character of it.
    const TINTS = [ '#7D848D', '#8E8276', '#AEB6C0', '#AC8563', '#7D848D', '#C3B9AD' ];
    const aim = ( fill: string, d: number ): number =>
      // RAISED from 1.52..2.14 — see buildPrague.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 2.20 + d * 1.40 ).toFixed( 3 ) );

    this.petals = [];

    // FIVE rows of columns, each row a short colonnade with its feet on one line.
    // The rows are offset from each other so no two line up vertically, and every
    // row has a different number of columns in it.
    // PUFFINS on a jittered grid, the way every other wallpaper in this run is
    // built. It replaced rows of hexagonal basalt columns.
    //
    // The columns went with the sea stacks — same subject, same verdict — and the
    // colonnade LAYOUT went with them, which is worth saying separately: rows of
    // groups is a harder thing to distribute than a grid of single objects, and
    // its first pass put five clumps on a diagonal and left two thirds of the room
    // empty. A grid of birds cannot fail that way.
    const mx = 90, my = 96;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // 0.55..1.05, not 0.34..0.70. A puffin's whole identification is its
          // bill, and a bill is a fifth of the bird — under about sixty units the
          // bill is three pixels and the grain comes back as a small grey mouse.
          s: +( 0.55 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.50 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // A HEXAGON — one column seen end-on. The cluster was drawn here for a pass
      // and the owner asked for something simpler; this is the simplest mark the
      // subject has and it is still the subject. See BASALT_HEX.
      //
      // A SMALL turn, and only a small one. A hexagon has six-fold symmetry so it
      // has no up to lose, but a field of them all at the same angle reads as a
      // printed tile, and one turned past about 20 degrees stops sitting flat.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 34 );
      const place = `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
        + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-60)`;
      this.petals.push( {
        d: BASALT_HEX,
        transform: place,
        fill, o: aim( fill, depth ),
      } );
    }

    // where every bird landed, so a fish is never dropped on top of one.
    //
    // The radius is TIGHT — a bird's own footprint plus a little air. Set at the
    // usual 34 + s*110 it reached 150 units, and with twenty birds spread over the
    // canvas that rejected all but three of the twenty-six fish: the second element
    // stopped existing. A clearance rule is meant to stop a collision, not to
    // reserve half the room.
    const taken = pts.map( ( q ) => ( { x: q.x, y: q.y, r: 22 + q.s * 62 } ) );

    // the SAND EELS between them — puffins carry them home crosswise in the bill, a
    // NO FISH (owner 2026-08-16: "6genler yeterli"). A shoal used to swim between
    // the grains as a second element, and it was the wrong one twice over: a fish
    // at wallpaper size is a dash, and this scene is a BEACH — the one place on
    // the island where you are looking at rock rather than into water.
    //
    // The hexagons carry it alone now, which is what this family's own rule asks
    // for ([[line-wallpaper-rules]]): one grain, and it is the hero.
  }

  private buildSydney( p: Product ): void {
    // The skin's own talk room (see line-color-set's SYDNEY). This component
    // paints NO sky — that colour IS the harbour, and every tint below aims at it.
    this.skinColor = '#D3EAF2';   // SAMPLED: the skin's pale-blue talk room

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.5124;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The harbour at three depths plus the theme's warm pair, and the warm ones
    // are deliberately rare: they are one sail in seven, the way a tan sail is
    // one boat in seven on a real harbour.
    // Two YELLOWS among the blues (owner 2026-08-16). They are not decoration
    // borrowed from nowhere: the owner's sampled skin puts a bright yellow on the
    // outgoing chat bubble and the passcode boats carry a yellow jib, so the sail
    // ties the wallpaper to both. Two of eight — enough that the eye finds them,
    // few enough that they stay the accent rather than the pattern.
    const TINTS = [ '#73B9D0', '#0F91C7', '#E3BC3C', '#9FCEDD', '#0E5E76',
                    '#73B9D0', '#C9A227', '#5FAFC8' ];
    const aim = ( fill: string, d: number ): number =>
      // RAISED from 1.30..1.80. See buildPrague for why every wallpaper in this
      // run started too faint.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.56 + d * 0.74 ).toFixed( 3 ) );

    const mx = 78, my = 82;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // 0.78..1.33, and this number is the whole anti-fin argument.
          //
          // A yacht is mostly SAIL: the hull is sixteen units of a hundred-unit
          // drawing. At the old 0.42..0.82 the smallest grains put a seven-pixel
          // hull under a fifty-pixel triangle, and seven pixels is nothing — so
          // the wallpaper came back as a field of triangles again, which is the
          // shark fin the Opera House shell was replaced for. The boat only stops
          // being a fin when its hull and its second sail both read, and that
          // takes size rather than cleverness.
          // SMALLER (owner 2026-08-16). They ran 0.78..1.33, which put a boat at
          // 90 to 150 units on a 1482-wide canvas — big enough that six of them
          // filled the phone's slice and the wallpaper read as a poster rather
          // than as a surface a chat bubble sits on.
          s: +( 0.46 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.34 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // A sail HEELS — it leans away from the wind — so unlike Prague's windows
      // these do tilt, and unlike Salda's shells they all tilt the SAME way. A
      // field of boats leaning in one direction is a breeze; leaning both ways it
      // is a squall, and a squall behind a chat is busier than it looks.
      // A sail HEELS — it leans away from the wind — so these tilt again, and they
      // all tilt the SAME way. A field of boats leaning in one direction is a
      // breeze; leaning both ways it is a squall, and a squall behind a chat is
      // busier than it looks.
      const lean = 3 + Math.round( fr( i2 * 0.5497004779 + 0.31 ) * 9 );
      this.petals.push( {
        // A BOAT, which is what this wallpaper wanted all along.
        //
        // It drifted one shell of the Opera House first, and a field of those over
        // pale water read as SHARK FINS — in an Australian theme. Then it drifted
        // the whole building, which fixed the fin and gave the room twenty small
        // grey clumps. A landmark does not survive being repeated; a boat is a
        // thing there are supposed to be a lot of, and twenty of them on a pale
        // harbour is simply a harbour.
        // ALWAYS BOTH SAILS (owner 2026-08-16). This used to drop to YACHT_SIMPLE
        // — hull, mast and mainsail only — under a scale of 0.72, on the argument
        // that a jib at that size is a sliver the rasteriser eats. Once the whole
        // fleet was shrunk to 0.46..0.80 that threshold caught most of it and the
        // wallpaper came out as a mix of sloops and one-sail dinghies. Two sails
        // is what makes this the same boat the tab bar and the passcode draw, and
        // a boat that loses one is a different boat.
        d: YACHT,
        transform: `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
          + ` rotate(${ lean }) scale(${ pt.s }) translate(-57,-64)`,
        fill, o: aim( fill, depth ),
      } );
    }

    // NO RIPPLES (owner 2026-08-16). Thirty flat ellipses used to lie between the
    // boats, and they were the wrong second element for two reasons: a ripple is
    // not recognisable on its own — the rule this family keeps relearning
    // ([[line-wallpaper-rules]]) — so at wallpaper size they read as smudges, and
    // the talk room is ALREADY water. Drawing waves on it is drawing the
    // background twice. The boats are the whole grain now.
  }

  private buildPrague( p: Product ): void {
    // The skin's own talk room (see line-color-set's PRAGUE). This component
    // paints NO sky — that colour IS the night, and every tint below aims at it.
    this.skinColor = '#131313';   // SAMPLED: LINE's near-black, not the derived navy

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.2088;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // Mostly the lamp golds, with the theme's colder blues behind them — windows
    // at two distances rather than one flat scatter. Nothing bright: a chat
    // bubble sits on this, and a lit window behind a message is the wallpaper
    // nobody can read a message on.
    // The terracotta #DF865B is GONE (owner 2026-08-15). It was the only warm red
    // in the set and it did not read as lamplight — a red lamp on a near-black
    // sky reads as a warning light, not a gas lantern. What is left is two golds,
    // two cold blues, a sky blue and a parchment.
    // #8A8CA0 and #C08F45 are the PASSCODE's second and third lamps — the grey of
    // a mantle just catching and the lamplight a step past it, two rungs of that
    // screen's cold-iron-to-gold ramp (owner 2026-08-15). They are the tones here
    // that belong to a lamp rather than to the night around it, and they keep the
    // wallpaper and the lock screen speaking one set.
    const TINTS = [ '#EFBA5A', '#5E7BA8', '#E3CA77', '#8A8CA0', '#C08F45',
                    '#4C6C96', '#74ADF8', '#DCCF9D' ];
    const aim = ( fill: string, d: number ): number =>
      // RAISED from 1.50..2.16, and this is the note the rest of the run points
      // at.
      //
      // Seven of the ten wallpapers in this batch shipped a first pass at a
      // contrast window somewhere around 1.3..1.8, and on review most of them were
      // effectively BLANK — a few faint marks on an otherwise empty room. The
      // mistake was reading those numbers as "subtle". A ratio of 1.5 against a
      // near-black talk room is an ink about two per cent off the background;
      // subtle is what 2.5 looks like, and 1.5 is invisible.
      //
      // Etna hit this first and hardest (its sky sits at relative luminance 0.006)
      // and its fix went to 2.4..4.2. The dark skins here need the same treatment
      // in proportion.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 2.20 + d * 1.50 ).toFixed( 3 ) );

    // Sakura's structure — a jittered grid, then relaxed apart — because coverage
    // has to be even without anything sitting on a grid line, and because plain
    // jitter leaves bare patches no seed hunt ever fixes.
    const mx = 78, my = 82;
    // SIX by FOUR. Twenty was too sparse — the phone shows only the middle
    // vertical slice of this canvas, so it held about eight and the wallpaper read
    // as empty night with a few marks in it. Thirty was then too many. This is the
    // step between (owner 2026-08-15), and it is where the lamps stop being a
    // texture and stay a scatter.
    const cols = 6, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // The FLOOR comes up, the ceiling barely moves (owner 2026-08-15: the
          // smallest ones a bit bigger). At 0.36 a lamp is 43 units of a canvas
          // 1482 wide, and once the panes were punched out of it there was not
          // enough left at that size to read as a lantern — the holes closed up
          // and it went back to being a speck. 0.48 is where the four panes
          // survive, which is the size this drawing is now bounded by.
          s: +( 0.48 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.26 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // A SMALL lean, alternating, and never more than about eight degrees (owner
      // 2026-08-15). This wallpaper shipped with no rotation at all on the
      // argument that a street lamp stands where it was planted and a tilted one
      // is a lamp a car has hit — which is true of ONE lamp on a street. A field
      // of thirty identical uprights is a fence, and the small lean is what turns
      // it back into a scatter. Opened from 16 degrees of spread to 26 on the
      // owner's second look — which lands it on Ocean's number after all. The
      // original argument for keeping it tighter (a medusa has no up to lose, a
      // lamp does) held for a lamp seen straight on; at a third of its box, in a
      // field of two dozen, a wider lean is what stops the rows lining up.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.41 ) - 0.5 ) * 26 );
      const place = `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
        + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-64)`;
      const o = aim( fill, depth );
      this.petals.push( { d: LAMP_SIMPLE, transform: place, fill, o } );
      // AND THE GLASS, the way the passcode lamps carry theirs (owner
      // 2026-08-15). LAMP_SIMPLE punches its panes out, so this second path drops
      // light back into the holes — a lit lantern rather than an empty frame.
      // Brighter than the ironwork and drawn at the same alpha, so the pane reads
      // as the source of the light and the frame as the thing around it.
      this.petals.push( {
        d: LAMP_GLASS, transform: place,
        fill: mixHex( fill, '#FFE9B0', 0.55 ),
        o: +Math.min( 1, o * 1.25 ).toFixed( 3 ),
      } );
    }

    // the MOTES: specks of light drifting off the lamps, and a second element this
    // wallpaper never had — the lancet version let its tracery do that job, and a
    // field of one shape at several sizes is a pattern rather than a surface.
    for ( let k = 0; k < 30; k++ ) {
      const x = 60 + fr( k * 0.6180339887 + 0.53 ) * ( this.W - 120 );
      const y = 60 + fr( k * 0.7548776662 + 0.17 ) * ( this.H - 120 );
      const sc = +( 0.18 + fr( k * 0.4142 + 0.31 ) * 0.20 ).toFixed( 3 );
      const fill = k % 3 === 0 ? '#DCCF9D' : '#EFBA5A';
      this.petals.push( {
        d: LAMP_MOTE,
        transform: `translate(${ Math.round( x ) },${ Math.round( y ) })`
          + ` rotate(${ Math.round( fr( k * 0.271 ) * 90 ) }) scale(${ sc }) translate(-60,-60)`,
        fill, o: aim( fill, 0.1 + fr( k * 0.3819 ) * 0.3 ),
      } );
    }
  }

  private buildSalda( p: Product ): void {
    // The skin's own talk room (see line-color-set's SALDA), SAMPLED. This
    // component paints NO sky — that colour IS the sky, and every tint below aims
    // at it. It was #EDF7F8, a palest turquoise; the phone draws a warm CREAM, so
    // every tint here is now aiming at a warm ground rather than a cool one.
    this.skinColor = '#FFF8E8';

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.7391;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The theme's own shore and lake, and nothing bright: this wallpaper sits
    // under chat bubbles, and the palette's saturated turquoise belongs to the
    // menu strip where nothing has to be read on top of it.
    // REVISED: the DRIFTWOOD BROWN is gone. #8F6055 was the darkest thing on a
    // theme whose whole subject is a white shore, and on this pale talk room it
    // read as a stain rather than as a shell.
    //
    // These stay MID-toned rather than the true creams the passcode uses, and the
    // reason is the ground: alphaForContrast mixes the SKY toward the ink, so on a
    // #EDF7F8 talk room a cream ink at full alpha still only reaches about 1.1:1.
    // A cream shell is possible on the passcode's deep lake and impossible here.
    // What is possible is the warm end of the shell family — tan, stone, sand —
    // against the lake blues.
    const TINTS = [ '#A59379', '#7FC3D2', '#C08A62', '#3FAFC6', '#C6B79B',
                    '#9DCFDB', '#D6B892' ];
    const aim = ( fill: string, d: number ): number =>
      // RAISED from 1.34..1.80 — see buildPrague. The shells were there all along;
      // at that window they were the same colour as the water they were on, so
      // what the eye found was the pebbles, which are darker.
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.60 + d * 0.78 ).toFixed( 3 ) );

    // Sakura's structure — a jittered grid, then relaxed apart — because coverage
    // has to be even without anything sitting on a grid line, and because plain
    // jitter leaves bare patches no seed hunt ever fixes.
    const mx = 76, my = 80;
    // SIX by FIVE, and each shell SMALLER (owner 2026-08-15: more of them, but
    // smaller). This went 5x4 -> 4x3 -> here in one evening, and the middle step
    // is the useful one to record: at twelve big shells the phone's slice held
    // about four, they all happened to be the lake blues, and the warm half of the
    // palette never made it onto a screen at all.
    //
    // Thirty small ones fix both halves of that. The slice holds a dozen, and the
    // TINT INDEX below is taken from row+col rather than from the running counter,
    // so neighbours are always a step apart in the list and no vertical band of
    // the canvas can come out one colour.
    const cols = 6, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // A wide size range: shells on a beach really do come at every size, and
          // unlike Ocean's medusae — where a fifth-size animal reads as a
          // different species — a small shell is just a small shell.
          // BIGGER than the 0.22..0.56 this started on. LINE covers a ~0.46-aspect
          // screen with this 1.11-aspect image, so a shell at 0.22 of its box is
          // about twenty units on a canvas a thousand wide — which, rotated to
          // whatever angle it landed at, is a blob. A shell has to be big enough
          // for its flutes to survive.
          // BIGGER again. At 0.34..0.70 the shells came back as coloured specks
          // among the pebbles — the pebbles were winning a field that is supposed
          // to be shells. 0.50..0.98 puts a shell at 47..92 units, which is the
          // range Ocean's jellyfish and Sydney's sails occupy and the size at
          // which the flutes survive at all.
          // SMALLER now that there are thirty of them and the pebbles are gone.
          // The pebble argument no longer applies — there is nothing left to lose
          // a size contest to — and the floor is the flutes: 0.40 puts a shell at
          // 38 units, which still carries them.
          s: +( 0.40 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.4 ) * 0.34 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.4 );
      // The tint comes from ROW + COLUMN, not from the running counter. With the
      // counter, a column of the grid walks the list in steps of `cols`, which on
      // some grid sizes hands one vertical band a single hue — and a vertical band
      // is exactly what the phone shows, since LINE crops this canvas to its middle
      // slice. Adding the row makes every neighbour one step apart in both
      // directions, so no slice can come out one colour.
      const fill = TINTS[ ( Math.floor( i2 / cols ) + ( i2 % cols ) ) % TINTS.length ];
      // A shell lying on sand really has landed at whatever angle it landed at,
      // and the first pass turned these through the full circle for exactly that
      // reason. It was wrong, and the way it was wrong is worth keeping: a scallop
      // upside down is a blob. Its whole silhouette is a fluted rim ABOVE a hinge,
      // and inverted there is nothing left for the eye to name.
      //
      // So they turn through 120 degrees, not 360 — enough that no two are the
      // same picture, not so much that any of them stops being a shell. Tallinn's
      // flake has the freedom this does not (a six-fold shape has no up to lose),
      // and Ocean's jellyfish has even less of it (turned far it reads as dead).
      // NARROWED from a hundred and twenty degrees to fifty.
      //
      // A scallop has an up — the hinge — and turned far enough it stops being a
      // shell and becomes a lobed blob. The wide window was justified on the
      // grounds that no two grains should be the same picture, and that is true
      // and it is not worth a shell for it: what came back was a field of round
      // splotches, and the owner had already asked twice for the shells to look
      // like shells.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 50 );
      const place = `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
        + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-64)`;
      // ALWAYS the full nine-lobe shell now. The five-lobe version existed for
      // grains too small to carry the detail; the grains are half again as big as
      // they were, so nothing here is that small any more.
      this.petals.push( { d: SHELL, transform: place, fill, o: aim( fill, depth ) } );
      // THE RIBS, cut back in the room's own colour.
      //
      // The tab and the passcode dots got their radial grooves when the owner
      // asked for them; the wallpaper did not, because it paints each grain in one
      // flat fill and ribs drawn in that fill are invisible. Reynisfjara already
      // solves this — it paints its columns' shadowed faces in `skinColor` — and
      // the same trick gives a scallop its grooves for one extra path per grain.
      this.petals.push( {
        d: SHELL_RIBS, transform: place,
        fill: this.skinColor, o: +( ( 0.30 + depth * 0.22 ) ).toFixed( 3 ),
      } );
    }

    // NO PEBBLES. There were 26 of them scattered between the shells, and at the
    // size they render on a phone they were not stones — they were grit. The
    // owner asked for them gone (2026-08-15) and the wallpaper is better without
    // them: this scene's grain is the SHELL ([[line-wallpaper-rules]]), and a
    // second element that is not recognisable on its own only ever competes with
    // it. Salda's Home tab, passcode dots and wallpaper now all draw one object.
  }

  private buildOcean( p: Product ): void {
    // The skin's own talk room (see line-color-set's OCEAN), SAMPLED. This
    // component paints NO sky — that colour IS the sky, and every tint below aims
    // at it. It was #0E2B30 until the owner's screenshots arrived; going from a
    // near-black sky to a near-white one is why the tints below are a different
    // set rather than the same set at different alphas.
    this.skinColor = '#EDFAF9';

    // ONE knob for "generate another one". Math.random is forbidden here: it
    // would hand back a different wallpaper on every rebuild and the exported
    // asset would stop matching the preview.
    const SEED = 0.3177;
    const fr = ( v: number ) => { const w = v + SEED; return w - Math.floor( w ); };

    // The theme's own colour circles taken DOWN, not up. The first set was the
    // bioluminescent one — #5FD3B4, #8FE0F0, #C79BE0 and the rest — and against
    // this sky none of them can reach even the bottom of the window at full
    // opacity: alphaForContrast clamps at alpha 1 and hands back roughly 1.1, so
    // every animal came out a ghost. The owner's screenshot of the empty chat is
    // that failure, photographed.
    //
    // Same five hues, same order, deepened until each has headroom over the top of
    // the window (2.4 to 4.1 at full opacity) and the solver has something to
    // dial. Still nothing warm beyond the coral pink: the coral garden keeps the
    // menu strip to itself.
    const TINTS = [ '#2E8B7E', '#4FB3A6', '#0BA0A5', '#8E6BA8', '#2E8B7E',
                    '#C9738F', '#3E9FB8' ];
    // The PALE-skin window (~1.6 .. 2.3), not the near-black one. Unchanged in
    // form from the first pass, which had it right by luck — the numbers were
    // aimed at a dark sky but they happen to be the pale band's numbers.
    const aim = ( fill: string, d: number ): number =>
      LineChatBgComponent.alphaForContrast( fill, this.skinColor,
        +( 1.55 + d * 0.70 ).toFixed( 3 ) );

    // Sakura's structure — a jittered grid, then relaxed apart — because coverage
    // has to be even without anything sitting on a grid line, and because plain
    // jitter leaves bare patches no seed hunt ever fixes.
    // FIVE columns, not four. LINE covers a ~0.46-aspect screen with this
    // 1.11-aspect image, so only the middle vertical slice of it is ever on a
    // phone — sixteen animals across the whole canvas is nine on the screen, and
    // nine is a sparse wallpaper however well each one is drawn.
    const mx = 74, my = 78;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];

    const pts: { x: number, y: number, s: number, i: number }[] = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.05 + fr( i * 0.6180339887 + 0.19 ) * 0.90;
        const jy = 0.05 + fr( i * 0.7548776662 + 0.43 ) * 0.90;
        pts.push( {
          i,
          x: mx + ( ( c + jx + ( r % 2 ) * 0.5 ) % cols ) * cellW,
          y: my + ( r + jy ) * cellH,
          // A narrower size range than Tallinn's snow. Snow falls at every
          // distance at once; a medusa is a fixed-size animal, and one drawn at a
          // fifth of its neighbour reads as a different species rather than as
          // the same one further away.
          s: +( 0.30 + Math.pow( fr( i * 0.3819660112 + 0.27 ), 1.3 ) * 0.28 ).toFixed( 3 ),
        } );
      }
    }
    const target = Math.sqrt( ( this.W - 2 * mx ) * ( this.H - 2 * my ) / pts.length ) * 1.02;
    for ( let pass = 0; pass < 6; pass++ ) {
      for ( const a of pts ) {
        let dx = 0, dy = 0;
        for ( const b of pts ) {
          if ( a === b ) { continue; }
          const ux = a.x - b.x, uy = a.y - b.y;
          const d = Math.hypot( ux, uy ) || 1;
          if ( d < target ) {
            const push = ( target - d ) / target;
            dx += ux / d * push * target * 0.5;
            dy += uy / d * push * target * 0.5;
          }
        }
        a.x = Math.max( mx, Math.min( this.W - mx, a.x + dx * 0.24 ) );
        a.y = Math.max( my, Math.min( this.H - my, a.y + dy * 0.24 ) );
      }
    }

    for ( const pt of pts ) {
      const i2 = pt.i;
      const depth = Math.pow( fr( i2 * 0.3819660112 + 0.27 ), 1.3 );
      const fill = TINTS[ i2 % TINTS.length ];
      // A SMALL lean, alternating, and never a full turn: a jellyfish has an up.
      // Tallinn's flakes tumble through 60 degrees because a six-fold shape has
      // no up to lose; turn this animal past about 15 and it reads as dead.
      const rot = Math.round( ( fr( i2 * 0.5497004779 + 0.31 ) - 0.5 ) * 26 );
      this.petals.push( {
        d: pt.s < 0.37 ? JELLYFISH_SIMPLE : JELLYFISH,
        transform: `translate(${ Math.round( pt.x ) },${ Math.round( pt.y ) })`
          + ` rotate(${ rot }) scale(${ pt.s }) translate(-60,-60)`,
        fill, o: aim( fill, depth ),
      } );
    }

    // The bubbles: a second, much smaller element, rising in loose columns
    // between the animals. Drawn as OUTLINES (`sw`) rather than as filled discs —
    // a bubble is a shell of light, and filled at this size it reads as a bead.
    const BUBBLE = 'M60,44 a16,16 0 1,0 0.1,0 Z';
    for ( let v = 0; v < 9; v++ ) {
      const bx = 60 + fr( v * 0.618 + 0.11 ) * ( this.W - 120 );
      const n = 4 + Math.floor( fr( v * 0.4142 + 0.3 ) * 4 );
      for ( let k = 0; k < n; k++ ) {
        const j = v * 13 + k;
        const t = k / n;
        const sc = +( 0.10 + fr( j * 0.7548 ) * 0.16 ).toFixed( 3 );
        this.petals.push( {
          d: BUBBLE,
          transform: `translate(${ Math.round( bx + Math.sin( t * 4 + v ) * 34 ) },`
            + `${ Math.round( this.H - 60 - t * ( this.H - 200 ) - fr( j * 0.271 ) * 70 ) })`
            + ` scale(${ sc }) translate(-60,-44)`,
          // A pale bubble on a near-white sky is not a bubble, it is nothing.
          // Deepened with the animals, and the opacity range lifted with it so the
          // outline still closes into a ring at this stroke width.
          fill: '#4FA79B',
          o: +( 0.30 + fr( j * 0.3819 ) * 0.18 ).toFixed( 3 ),
          sw: 5,
        } );
      }
    }
  }

  private buildSequoia( p: Product ): void {
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    // The flat sky to paste into LINE's talk-room field: the warm pale of the
    // GREEN skin the owner picked (sampled 2026-08-13), NOT the theme's own
    // editor colour.
    this.skinColor = '#EBEBDE';
    const leaf = cp[ 1 ] || '#6E9F56';
    const moss = cp[ 0 ] || '#405133';
    const gold = cp[ 2 ] || '#A68F59';
    // ALL GREEN, and greener than the first all-green pass (owner 2026-08-13):
    // the greys the palette's moss reads as on a pale sky are gone, so the range
    // runs from the theme's leaf green down to its deepest forest, nothing muddy
    // in between.
    const tints = [ '#6E9F56', '#4E7A38', '#7FB45C', '#33502A', '#5E8B45', '#8CC63F', '#456B31' ];

    // Built to SAKURA's structure (owner 2026-08-13: "sakura'nın arka planı gibi
    // olsun"), because that is the drift this whole family is tuned around: a
    // jittered grid, so coverage is even across the canvas yet nothing sits on a
    // grid line, with rotation clustered around one prevailing wind angle so the
    // leaves tumble together instead of pointing every which way.
    //
    // Two earlier tries were wrong in opposite directions — threading them along
    // S-curves lined them into visible chains, and a rejection-sampled scatter
    // left the canvas patchy. Sakura's grid solves both, and it is the reason its
    // own petals read as a breeze.
    //
    // Note the leaf is authored around the shared 120 box's centre, so every
    // transform re-centres it before scaling; Sakura's petal is authored at the
    // origin and needs no such step.
    const fr = ( v: number ) => v - Math.floor( v );
    // Keep a clear margin from every edge so a centred/cut image never hard-clips
    // a leaf (beyond it there is only the transparent colour skin).
    const mx = 70, my = 80;
    const windAngle = 35;              // degrees, the prevailing drift direction
    const cols = 6, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.12 + fr( i * 0.6180339887 + 0.11 ) * 0.76;
        const jy = 0.12 + fr( i * 0.7548776662 + 0.37 ) * 0.76;
        const x = Math.round( mx + ( c + jx ) * cellW );
        const y = Math.round( my + ( r + jy ) * cellH );
        const rot = Math.round( windAngle + ( fr( i * 0.5497004779 + 0.5 ) - 0.5 ) * 150 );
        const sc = +( 0.26 + fr( i * 0.3819660112 + 0.2 ) * 0.3 ).toFixed( 3 );
        const fill = tints[ i % tints.length ];
        // Bigger leaves are nearer → a touch more opaque.
        const o = +( 0.26 + ( sc - 0.26 ) / 0.3 * 0.22 ).toFixed( 2 );
        this.petals.push( {
          transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc }) translate(-60,-60)`,
          fill, o,
        } );
      }
    }
  }

  /**
   * Everest: CLOUDS drifting over the chat skin (owner 2026-08-12, after
   * snowflakes, a ridgeline landscape, tiny summits, plain dots and contour
   * rings were each tried and dropped) — kept in the scattered structure that
   * works for Sakura's petals and Tokyo's twinkles.
   *
   * It is the right motif for this theme: Everest is the one place you look DOWN
   * at cloud, so the wallpaper puts the viewer at the summit without drawing the
   * summit. Clouds have no hard edge and no silhouette to decode, so they stay
   * out of the way of the bubbles, and dawn light on cloud is exactly where this
   * theme's warm accent belongs — a few of them catch it.
   *
   * Small and many rather than few and large (owner 2026-08-12: the first pass
   * drew clouds half the phone wide, which read as blobs) — a little cloud is
   * cute, a big one is furniture.
   */
  private buildEverest( p: Product ): void {
    const t: any = p.designTokens || {};
    const icy = t.primaryColor || '#7db1cf';

    // Flat "sky" = the palest wash of the glacier blue; this is the value to
    // paste into the LINE color-skin talk-room background field.
    this.skinColor = mixHex( '#FFFFFF', icy, 0.10 );

    // No white: the skin behind these is white, so a white cloud is a hole. Two
    // of the seven catch the sunrise — cloud is where this theme's warmth lives.
    const tints = [
      mixHex( '#FFFFFF', icy, 0.42 ), '#D5E7F1', mixHex( '#FFFFFF', '#ED7E5A', 0.34 ),
      mixHex( '#FFFFFF', icy, 0.58 ), '#D5E7F1', mixHex( '#FFFFFF', icy, 0.34 ),
      mixHex( '#FFFFFF', '#ED7E5A', 0.22 ),
    ];

    const fr = ( v: number ) => v - Math.floor( v );
    const mx = 80, my = 90;
    const cols = 4, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        // Tighter jitter than the petal designs use: a cloud is wide, and two
        // that drift into each other read as one smudged blob.
        const jx = 0.2 + fr( i * 0.6180339887 + 0.11 ) * 0.6;
        const jy = 0.22 + fr( i * 0.7548776662 + 0.37 ) * 0.56;
        const x = Math.round( mx + ( c + jx ) * cellW );
        const y = Math.round( my + ( r + jy ) * cellH );
        // Size is baked into the path, and clouds never rotate — a tilted cloud
        // reads as a smudge.
        const rad = Math.round( 17 + fr( i * 0.3819660112 + 0.2 ) * 13 );
        const fill = tints[ i % tints.length ];
        // Bigger cloud = nearer = a touch more present, but all of it stays low.
        const o = +( 0.28 + ( rad - 17 ) / 13 * 0.16 ).toFixed( 2 );
        this.petals.push( {
          transform: `translate(${ x },${ y })`,
          d: LineChatBgComponent.cloud( rad, i + 1 ),
          fill, o,
        } );
      }
    }
  }

  /** Tokyo: soft twinkles drifting over the mauve chat skin (its own kawaii
   *  scene, mirroring Sakura's petals). */
  private buildTokyo( p: Product ): void {
    this.skinColor = '#B99EC5';                       // == the chat surface (mauve)
    const fr = ( v: number ) => v - Math.floor( v );
    const mx = 70, my = 80;
    const cols = 5, rows = 4;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    const tints = [ '#FFFFFF', '#EFE2F5', '#DAC6E8', '#CBAEE0' ];  // soft white → lilac
    this.petals = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        const jx = 0.12 + fr( i * 0.6180339887 + 0.11 ) * 0.76;
        const jy = 0.12 + fr( i * 0.7548776662 + 0.37 ) * 0.76;
        const x = Math.round( mx + ( c + jx ) * cellW );
        const y = Math.round( my + ( r + jy ) * cellH );
        const rot = Math.round( fr( i * 0.5497004779 + 0.5 ) * 90 );
        const sc = +( 1.5 + fr( i * 0.3819660112 + 0.2 ) * 3.0 ).toFixed( 2 );  // small
        const o = +( 0.28 + fr( i * 0.41 + 0.3 ) * 0.4 ).toFixed( 2 );          // subtle
        this.petals.push( { transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc })`, fill: tints[ i % tints.length ], o } );
      }
    }
  }

  /**
   * Miami: palm FRONDS drifting over the chat skin.
   *
   * The family rule — the hero stays whole on the tab and the passcode, a
   * fragment of it drifts here. Sakura sheds petals, Autumn sheds leaves,
   * Roraima sends up gulls, Miami sheds fronds. It is also the one Miami
   * fragment that floats without implying a horizon, which is the trap that
   * killed the flying-mountain idea on Roraima: a boat or a building scattered
   * across a wallpaper reads as objects hanging in the air.
   */
  private buildMiami( p: Product ): void {
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const pink = cp[ 1 ] || '#FF5FA2';
    const aqua = cp[ 3 ] || '#00D1C1';
    const peach = cp[ 2 ] || '#FE8078';

    // The flat sky to paste into LINE's talk-room field.
    this.skinColor = '#FFF2F6';

    // Three hues, not two (owner 2026-08-13: "şeftali rengi yapraklar da
    // olsun"). All carried a long way toward the skin: a frond covers far more
    // area than a petal, so at petal opacity this would read as a pattern
    // fighting every bubble on top of it rather than as a background.
    // The cycle is walked one tint per frond, so its makeup IS the mix on the
    // canvas. Aqua was one entry in five AND the weakest blend of the three, so
    // it read as almost absent (owner 2026-08-13: "yeşil çok az oldu"). Now it
    // is two in six and mixed as strongly as the others.
    const tints = [
      mixHex( this.skinColor, pink, 0.30 ),
      mixHex( this.skinColor, aqua, 0.34 ),
      mixHex( this.skinColor, peach, 0.34 ),
      mixHex( this.skinColor, pink, 0.22 ),
      mixHex( this.skinColor, aqua, 0.28 ),
      mixHex( this.skinColor, peach, 0.26 ),
    ];

    // NON-OVERLAPPING scatter (owner 2026-08-13: "üst üste gelmesin yapraklar").
    //
    // A jittered grid cannot promise that. It spaces the ANCHORS evenly, but a
    // frond is long and thin and reaches most of a cell away from its anchor, so
    // two neighbours leaning toward each other still cross. This is rejection
    // sampling instead: propose a placement, work out the disc the blade
    // actually covers, and drop it if it touches one already taken.
    //
    // Deterministic throughout — these assets are captured from the live page,
    // so Math.random would hand back a different wallpaper on every rebuild.
    const mx = 90, my = 100;
    const TARGET = 26;
    const GAP = 14;                       // clear air between two blades

    let seed = 20260813;
    const rnd = () => {
      seed = ( seed * 1103515245 + 12345 ) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    const taken: { x: number, y: number, r: number }[] = [];
    this.petals = [];
    for ( let tries = 0; tries < 6000 && this.petals.length < TARGET; tries++ ) {
      const k = Math.floor( rnd() * MIAMI_FRONDS.length );
      const sc = +( 0.95 + rnd() * 1.05 ).toFixed( 2 );
      const rot = Math.round( rnd() * 360 );
      const x = Math.round( mx + rnd() * ( this.W - 2 * mx ) );
      const y = Math.round( my + rnd() * ( this.H - 2 * my ) );

      // The blade runs from its anchor out along its own axis, so the disc that
      // covers it is centred half a reach away, not on the anchor.
      const reach = MIAMI_FROND_REACH[ k ] * sc;
      const a = ( rot * Math.PI ) / 180;
      const cx = x + Math.cos( a ) * reach * 0.5;
      const cy = y + Math.sin( a ) * reach * 0.5;
      const r = reach * 0.5;
      if ( taken.some( t => Math.hypot( t.x - cx, t.y - cy ) < t.r + r + GAP ) ) { continue; }

      taken.push( { x: cx, y: cy, r } );
      this.petals.push( {
        d: MIAMI_FRONDS[ k ],
        transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc }) translate(-54,-44)`,
        fill: tints[ this.petals.length % tints.length ],
        o: +( 0.5 + ( sc - 0.95 ) / 1.05 * 0.28 ).toFixed( 2 ),
      } );
    }
  }

  private buildSakura( p: Product ): void {
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const pinkSoft   = t.primaryColor || '#E898B4';
    const lightPink  = t.surface      || '#F8DBE6';
    const brightPink = cp[ 0 ]        || '#FFC9DC';
    const rose       = cp[ 1 ]        || '#CB6B91';

    // Flat "sky" = a soft pink; this is the value to paste into the LINE
    // color-skin talk-room background field.
    this.skinColor = mixHex( '#FFFFFF', pinkSoft, 0.10 );

    // Petal / blossom tints (light → mid pinks) so they read on the flat sky.
    const pA = mixHex( '#FFFFFF', pinkSoft, 0.45 );
    const pB = lightPink;
    const pC = mixHex( '#FFFFFF', brightPink, 0.40 );
    const pD = mixHex( '#FFFFFF', rose, 0.35 );
    const tints = [ pA, pB, pC, pD ];

    const fr = ( v: number ) => v - Math.floor( v );
    // Keep a clear margin from every edge so a centered/cut image never hard-
    // clips a petal (the sky beyond a petal is the transparent color skin).
    const mx = 70, my = 80;

    // Petals drifting in the wind, spread EVENLY over the whole canvas via a
    // jittered grid (owner 2026-07-29: the earlier golden-ratio scatter bunched
    // them onto one central diagonal). Each grid cell gets one petal jittered
    // within it, so coverage is uniform yet still looks random. Petals tumble
    // (rotation clusters around a gentle wind angle) and vary in size so nearer
    // ones read larger/warmer — a soft breeze of blossoms across the sky.
    const windAngle = 35; // degrees, the prevailing drift direction
    const cols = 6, rows = 5;
    const cellW = ( this.W - 2 * mx ) / cols;
    const cellH = ( this.H - 2 * my ) / rows;
    this.petals = [];
    let i = 0;
    for ( let r = 0; r < rows; r++ ) {
      for ( let c = 0; c < cols; c++, i++ ) {
        // jitter within the cell (0.12..0.88) so petals never sit on a grid line.
        const jx = 0.12 + fr( i * 0.6180339887 + 0.11 ) * 0.76;
        const jy = 0.12 + fr( i * 0.7548776662 + 0.37 ) * 0.76;
        const x = Math.round( mx + ( c + jx ) * cellW );
        const y = Math.round( my + ( r + jy ) * cellH );
        const rot = Math.round( windAngle + ( fr( i * 0.5497004779 + 0.5 ) - 0.5 ) * 150 );
        const sc = +( 2.4 + fr( i * 0.3819660112 + 0.2 ) * 4.6 ).toFixed( 2 );
        const fill = tints[ i % tints.length ];
        // bigger petals are nearer → a touch more opaque.
        const o = +( 0.45 + ( sc - 2.4 ) / 4.6 * 0.4 ).toFixed( 2 );
        this.petals.push( { transform: `translate(${ x },${ y }) rotate(${ rot }) scale(${ sc })`, fill, o } );
      }
    }
  }
}
